// Pure logic for the Ask Bizminer panel (CHAT-12): SSE parsing, the message
// reducer, and the safe markdown-lite renderer. No Vue, no DOM, no network, so
// it runs under `node --test tests/` (see tests/chatCore.test.mjs).
//
// Protocol reference: chat.bizminer.com README, "SSE event protocol".
//   step      {id, label, state: 'running'|'done'}
//   text      {delta}
//   citation  {product, industry, area, salesClass, owned, appUrl, company?}
//   ownership {owned: true, charged: false}
//   confirm   {pendingId, product, industry, area, salesClass, company?, chargeText, subscription: {remaining},
//              expiresAt, subUserNote, subUserNoteText}
//   confirm   of a non-purchase action also carries {action: delete_my_data|overwrite_my_data|update_company_report, prompt}:
//             chargeText repeats the prompt and nothing on that card is a charge (see CARD_ACTIONS)
//   confirm_result {pendingId, outcome: purchased|already_owned|not_covered|declined|expired|rejected|deleted|saved,
//              charged: false|null, message, subscription?: {remaining}}
//             (only on a confirmation click stream; the card follows `outcome`, never the HTTP status)
//   not_covered {reason: no_reports_left|not_in_plan, combination, contactUrl}   (CHAT-18/c)
//   error     {code, message, retryable}   code subscription_required = show the teaser; retryable: true = the
//             subscription could not be read ("Try again"), not a real teaser
//   chart     {id, kind: 'bar'|'line', title, unit, series: [{name, points: [{label, value, source: {citationKey}}]}], dropped?}
//             (CHAT-19; arrives after the citations it points at; `text_reset` clears charts)
//   prospects {source: count|owned_list, count, rows[], combination, filters, buyUrl}   (PFD-235; a preview only, never a price)
//   not_covered with reason buy_in_app {combination, buyUrl}: a Prospect List is bought in the app, not in chat
//   text_reset {}   a refusal arrived after text or a citation: discard the answer text, citations, charts and prospects so far
//   done      {messageId}   always last

// ---------------------------------------------------------------------------
// SSE parsing
// ---------------------------------------------------------------------------

// Incremental parser. push() takes decoded text chunks of any size (frames may
// be split anywhere), flush() handles a final frame with no trailing blank line.
// Calls onEvent({name, data}) per frame; data is the parsed JSON, or null when
// the payload is not valid JSON. Comment lines (": keep-alive") are ignored.
export function createSseParser(onEvent) {
  let buffer = '';
  let name = null;
  let dataLines = [];

  function dispatch() {
    if (name === null && dataLines.length === 0) return;
    const raw = dataLines.join('\n');
    let data = null;
    if (raw !== '') {
      try {
        data = JSON.parse(raw);
      } catch (e) {
        data = null;
      }
    }
    const evt = { name: name || 'message', data };
    name = null;
    dataLines = [];
    onEvent(evt);
  }

  function line(l) {
    if (l === '') return dispatch();
    if (l.charAt(0) === ':') return;
    const i = l.indexOf(':');
    const field = i === -1 ? l : l.slice(0, i);
    let value = i === -1 ? '' : l.slice(i + 1);
    if (value.charAt(0) === ' ') value = value.slice(1);
    if (field === 'event') name = value;
    else if (field === 'data') dataLines.push(value);
  }

  return {
    push(chunk) {
      buffer += chunk;
      let idx;
      // Accept \n, \r\n and lone \r as line ends.
      while ((idx = buffer.search(/\r\n|\n|\r/)) !== -1) {
        const l = buffer.slice(0, idx);
        const sep = buffer.slice(idx).startsWith('\r\n') ? 2 : 1;
        // A lone trailing \r may be the first half of \r\n: wait for more.
        if (buffer.charAt(idx) === '\r' && idx + 1 === buffer.length) break;
        buffer = buffer.slice(idx + sep);
        line(l);
      }
    },
    flush() {
      if (buffer !== '') {
        line(buffer.replace(/\r$/, ''));
        buffer = '';
      }
      dispatch();
    },
  };
}

// ---------------------------------------------------------------------------
// Safe markdown-lite
// ---------------------------------------------------------------------------

export function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Input is ALREADY escaped. We only ever add <strong> and <code>.
function inline(escaped) {
  return escaped
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
}

function splitRow(line) {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|')) t = t.slice(0, -1);
  return t.split('|').map((c) => c.trim());
}

const isTableSep = (l) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l) && l.indexOf('-') !== -1;
const isTableRow = (l) => /^\s*\|.*\|\s*$/.test(l);
const bulletRe = /^\s*[-*•]\s+(.*)$/;
const orderedRe = /^\s*\d+[.)]\s+(.*)$/;

// Escapes everything first, then supports: paragraphs, **bold**, `code`,
// "- " / "1. " lists, "# " headings (rendered as bold paragraphs) and pipe
// tables. Raw HTML from the model is never passed through.
export function renderMarkdownLite(text) {
  const lines = String(text == null ? '' : text).replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let para = [];
  let i = 0;

  const flushPara = () => {
    if (para.length) {
      out.push('<p>' + inline(escapeHtml(para.join(' '))) + '</p>');
      para = [];
    }
  };

  while (i < lines.length) {
    const l = lines[i];

    if (l.trim() === '') {
      flushPara();
      i++;
      continue;
    }

    if (isTableRow(l) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushPara();
      const head = splitRow(l);
      i += 2;
      const rows = [];
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      let html = '<div class="bzc-table"><table><thead><tr>';
      head.forEach((h) => {
        html += '<th scope="col">' + inline(escapeHtml(h)) + '</th>';
      });
      html += '</tr></thead><tbody>';
      rows.forEach((r) => {
        html += '<tr>';
        head.forEach((_, c) => {
          html += (c === 0 ? '<th scope="row">' : '<td>') + inline(escapeHtml(r[c] || '')) + (c === 0 ? '</th>' : '</td>');
        });
        html += '</tr>';
      });
      html += '</tbody></table></div>';
      out.push(html);
      continue;
    }

    const b = bulletRe.exec(l);
    const o = orderedRe.exec(l);
    if (b || o) {
      flushPara();
      const tag = b ? 'ul' : 'ol';
      const re = b ? bulletRe : orderedRe;
      const items = [];
      let m;
      while (i < lines.length && (m = re.exec(lines[i]))) {
        items.push('<li>' + inline(escapeHtml(m[1])) + '</li>');
        i++;
      }
      out.push('<' + tag + '>' + items.join('') + '</' + tag + '>');
      continue;
    }

    const h = /^\s{0,3}#{1,6}\s+(.*)$/.exec(l);
    if (h) {
      flushPara();
      out.push('<p><strong>' + inline(escapeHtml(h[1])) + '</strong></p>');
      i++;
      continue;
    }

    para.push(l.trim());
    i++;
  }
  flushPara();
  return out.join('');
}

// ---------------------------------------------------------------------------
// Visibility gate
// ---------------------------------------------------------------------------

// users.BVR arrives from /session/authenticate as a bool. Be conservative with
// anything else: only clear falsy spellings count as "not BVR".
export function isBvrAccount(v) {
  if (v == null || v === false) return false;
  return !['', '0', 'false', 'no', 'null'].includes(String(v).toLowerCase());
}

// The ONE rule behind every entry point (top-bar button, report pill, panel):
// beta flag on, chat configured, a signed-in user, not a BVR account, and the
// chat service has not said not_in_beta. What the user then sees (the full panel
// or the locked teaser) is chatMode(). An academic (IP) session has no
// individual uid, so it needs only the user object: it gets the teaser.
export function computeChatVisible({ flag, denied, configured, user, userType }) {
  const academic = !!(userType && userType.academic);
  return !!(
    flag === true &&
    !denied &&
    configured &&
    user &&
    (user.userID || academic) &&
    !isBvrAccount(user.BVR)
  );
}

// ---------------------------------------------------------------------------
// Phones (CHAT-23)
// ---------------------------------------------------------------------------

// At this width and below the chat is a full-screen sheet opened from a
// floating button (the same number as the @media rules in AskBizminerPanel.vue).
export const PHONE_MAX_WIDTH = 600;

// The floating "Ask Bizminer" button. It follows the SAME visibility rule as the
// top-bar button (`visible` = flag on, not denied, not BVR, signed in), never
// shows on top of the open panel, and carries the lock for a confirmed teaser.
export function chatFabState({ visible, open, locked } = {}) {
  const show = !!visible && !open;
  const lock = show && !!locked;
  return {
    show,
    locked: lock,
    label: lock ? 'Ask Bizminer, included with a Bizminer subscription' : 'Ask Bizminer',
  };
}

// Where the full-screen sheet sits while the on-screen keyboard is up, so the
// composer stays above it. Phone browsers keep the layout viewport the same
// height when the keyboard opens and only shrink the VISUAL viewport, so the
// sheet is sized to that. Returns null when no keyboard is up (small gaps are
// just the browser's own toolbars): the sheet then uses 100dvh.
export const KEYBOARD_MIN_PX = 120;
export function keyboardViewportBox({ layoutHeight, viewportHeight, viewportTop } = {}) {
  const num = (n) => typeof n === 'number' && isFinite(n);
  if (!num(layoutHeight) || !num(viewportHeight) || viewportHeight <= 0) return null;
  if (layoutHeight - viewportHeight < KEYBOARD_MIN_PX) return null;
  return { height: Math.round(viewportHeight), top: Math.max(0, Math.round(num(viewportTop) ? viewportTop : 0)) };
}

// Where the teaser's buttons go (CHAT-18/e, D1): the existing Upgrade Account
// booking link (pages/Layout/MainNavbar.vue). Subscribe and Contact sales share it.
export const UPGRADE_URL = 'https://bizminer.pipedrive.com/scheduler/qllvp0s0/bizmineraccount-upgrade';
export const CONTACT_SALES_URL = UPGRADE_URL;

// D2: PostHog events for the two teaser buttons.
export const TEASER_EVENTS = { subscribe: 'chat_teaser_subscribe_click', contact: 'chat_teaser_contact_click' };

// 'full' = the chat panel, 'teaser' = the locked panel without a composer.
//  - an academic session is always the teaser (no uid, the backend is not called);
//  - else the server's `entitlement` (POST /session, GET /me) is authoritative;
//  - until the server has answered, a quick client guess from the
//    /session/authenticate payload: userType.subscriber, or a sub-user (whose
//    parent holds the subscription). `retail` is NOT the test: a subscriber can
//    be retail too, with 0 reports left.
export function chatMode({ userType, user, serverEntitlement } = {}) {
  if (userType && userType.academic) return 'teaser';
  if (serverEntitlement === 'subscriber') return 'full';
  if (serverEntitlement === 'teaser') return 'teaser';
  return (userType && userType.subscriber === true) || (user && user.is_sub_user) ? 'full' : 'teaser';
}

// P2: the teaser is shown as fact only once the server said so (or for an
// academic session, which is always a teaser and never calls the backend). Until
// then a client guess of "teaser" must not show the subscribe pitch.
export function teaserConfirmed({ userType, serverEntitlement } = {}) {
  return !!(userType && userType.academic) || serverEntitlement === 'teaser';
}

// What the teaser branch of the panel shows:
//  'checking' = unconfirmed guess, still connecting: "Checking your access…"
//  'error'    = unconfirmed guess and the connection failed (or gave no answer): error + Retry
//  'retry'    = the server said teaser because it could not read the subscription: "Try again"
//  'pitch'    = the server (or an academic session) confirmed a teaser: Subscribe / Contact sales
export function teaserView({ confirmed, retryable, connection } = {}) {
  if (!confirmed) return connection === 'idle' || connection === 'connecting' ? 'checking' : 'error';
  return retryable ? 'retry' : 'pitch';
}

// {entitlement: 'subscriber'|'teaser'|null, remaining: int|null, retryable}
// from a POST /session or GET /me body. null = the service did not say.
// retryable: true only means a teaser that was a fail-closed result.
export function readEntitlement(p) {
  const entitlement = p && (p.entitlement === 'subscriber' || p.entitlement === 'teaser') ? p.entitlement : null;
  const r = p && p.subscription && p.subscription.remaining;
  return {
    entitlement,
    remaining: typeof r === 'number' && isFinite(r) ? r : null,
    retryable: entitlement === 'teaser' && p.retryable === true,
  };
}

// 403 {error:'subscription_required'} (a ChatApiError) or the SSE `error`
// event {code:'subscription_required'}: the account has no subscription (any
// more). Not retryable: the panel switches to the teaser.
export function isSubscriptionRequired(x) {
  return !!x && x.code === 'subscription_required';
}

// What chat/setEntitlement gets for a subscription_required answer (P1). Only an
// explicit `retryable: true` (the server could not read the subscription) shows
// "Couldn't check your subscription. Try again" instead of the subscribe pitch.
export function entitlementFromError(x) {
  return { entitlement: 'teaser', retryable: !!x && x.retryable === true };
}

// The chat service (not_in_beta) or api.app (not_allowed) refused this user.
// Remembered against the user id, so it lasts the whole session (a remount of
// the navbar must not bring the button back) and ends on logout (RESET) or when
// a different user is signed in.
export function isDeniedFor(deniedUid, user) {
  return deniedUid != null && !!user && user.userID != null && String(user.userID) === String(deniedUid);
}

// ---------------------------------------------------------------------------
// Citations, links, labels
// ---------------------------------------------------------------------------

function nameOf(x) {
  if (x == null) return '';
  if (typeof x === 'string' || typeof x === 'number') return String(x);
  return x.name != null ? String(x.name) : '';
}

function industryOf(x) {
  if (x && typeof x === 'object') {
    if (x.name) return x.code ? x.name + ' (' + x.code + ')' : String(x.name);
    return x.code ? String(x.code) : '';
  }
  return nameOf(x);
}

// {locnum, name}: the name, or the location number when the name is unknown.
function companyOf(x) {
  if (x && typeof x === 'object') return x.name ? String(x.name) : x.locnum != null && x.locnum !== '' ? '#' + x.locnum : '';
  return nameOf(x);
}

export function citationLabel(c) {
  if (!c) return '';
  return [c.product, companyOf(c.company), industryOf(c.industry), nameOf(c.area), nameOf(c.salesClass)]
    .filter(Boolean)
    .join(' · ');
}

// An appUrl comes from the chat service; only follow it when it points back at
// this app or another bizminer.com host. Returns {external, href} or null.
export function safeAppLink(appUrl, currentOrigin) {
  if (!appUrl || typeof appUrl !== 'string') return null;
  try {
    const u = new URL(appUrl, currentOrigin || 'https://app.bizminer.com');
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (currentOrigin && u.origin === currentOrigin) {
      // `//evil.com/x` would be read as a scheme-relative URL (and `/\evil.com`
      // parses to `//evil.com`): collapse leading slashes so it stays a path.
      return { external: false, href: u.pathname.replace(/^\/+/, '/') + u.search + u.hash };
    }
    if (/(^|\.)bizminer\.com$/i.test(u.hostname)) return { external: true, href: u.href };
  } catch (e) {
    return null;
  }
  return null;
}

// D1: the one external host besides *.bizminer.com a contactUrl may point at, the
// Pipedrive booking page. https only, that exact host, no port, no credentials.
// Deliberately NOT part of safeAppLink, so citations cannot link there.
const PIPEDRIVE_HOST = 'bizminer.pipedrive.com';
function pipedriveLink(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:' || u.hostname.toLowerCase() !== PIPEDRIVE_HOST) return null;
    if (u.port || u.username || u.password) return null;
    return { external: true, href: u.href };
  } catch (e) {
    return null;
  }
}

// The Contact sales button of a not_covered card: the service's contactUrl when
// it is a bizminer.com link or the Pipedrive booking page, else the booking link.
export function contactSalesLink(contactUrl, currentOrigin) {
  return safeAppLink(contactUrl, currentOrigin) || pipedriveLink(contactUrl) || { external: true, href: CONTACT_SALES_URL };
}

// ---------------------------------------------------------------------------
// Daily limits (CHAT-06, PFD-218). `/me` and `/session` carry
// `limits {messagesLeft, messageLimit, resetAt, purchasesLeft, purchaseLimit}`.
// Money is never shown: the service also caps cost, but the panel only ever talks
// about messages and reports.
// ---------------------------------------------------------------------------

// The hint under the composer shows only when this many messages (or fewer) are left.
export const LOW_MESSAGES_AT = 10;

// "3 messages left today", only while 1..LOW_MESSAGES_AT remain (at 0 the limit card says it).
export function messagesLeftLabel(left) {
  if (typeof left !== 'number' || !isFinite(left)) return '';
  const n = Math.floor(left);
  if (n < 1 || n > LOW_MESSAGES_AT) return '';
  return n + (n === 1 ? ' message' : ' messages') + ' left today';
}

// An ISO time -> epoch ms, or null.
export function parseResetAt(iso) {
  if (typeof iso !== 'string' || iso === '') return null;
  const t = new Date(iso).getTime();
  return isNaN(t) ? null : t;
}

// The `limits` of a /me or /session body -> {messagesLeft, messageLimit, resetAt (ms|null),
// purchasesLeft, purchaseLimit} or null (absent or unreadable: the service sends null when it
// could not count). Counts are whole numbers, never negative.
export function normalizeLimits(body) {
  const l = body && typeof body === 'object' ? body.limits : null;
  if (!l || typeof l !== 'object') return null;
  const whole = (v) => (typeof v === 'number' && isFinite(v) ? Math.max(0, Math.floor(v)) : null);
  const messagesLeft = whole(l.messagesLeft);
  const messageLimit = whole(l.messageLimit);
  if (messagesLeft === null || messageLimit === null) return null;
  return { messagesLeft, messageLimit, resetAt: parseResetAt(l.resetAt), purchasesLeft: whole(l.purchasesLeft), purchaseLimit: whole(l.purchaseLimit) };
}

// When the composer stays shut because the counter already says 0 left: the reset time (ms)
// while it is in the future, else null.
export function limitUntilFrom(limits, now = Date.now()) {
  return limits && limits.messagesLeft === 0 && limits.resetAt != null && limits.resetAt > now ? limits.resetAt : null;
}

// What /me or /session says about the daily report limit, from the real purchase count:
//   a number  purchasesLeft is 0 and resetAt is ahead: hold Confirm off until then (ms)
//   null      purchasesLeft is above 0 (a counted claim failed or was reaped, so the count dropped), or
//             the hold has already passed: lift any hold now
//   undefined the count is unknown (or 0 with no usable time): say nothing, leave the hold as it is
// Only the purchase count ever holds Confirm; the message and cost limits never do.
export function purchaseUntilFrom(limits, now = Date.now()) {
  if (!limits || typeof limits.purchasesLeft !== 'number') return undefined;
  if (limits.purchasesLeft > 0) return null;
  if (limits.resetAt == null) return undefined;
  return limits.resetAt > now ? limits.resetAt : null;
}

// Orders the /me and /session limits reads against the events that set a hold. Every read takes a ticket when it
// is issued; a hold set by an event (the `purchase_limit` SSE event, a 429 daily_purchase_limit) takes the next
// number when it is set. So a read can tell whether it was issued before or after a hold. The service counts
// `purchasesLeft` the same way as the event (chat_purchase_log plus claims in flight, one list for both the
// refusal and /me), so a read issued after the hold agrees with it; only an older read can contradict it.
export function createLimitsGuard() {
  let seq = 0;
  let applied = 0;
  const holds = {};
  return {
    // A ticket for a read that is about to be issued.
    begin() {
      seq += 1;
      return seq;
    },
    // A hold (`key`) was just set by an event.
    noteHold(key) {
      seq += 1;
      holds[key] = seq;
    },
    // The hold was lifted or the session ended.
    forgetHold(key) {
      delete holds[key];
    },
    // A read has arrived: false when a newer read already applied (an out-of-order one is dropped whole).
    accept(ticket) {
      if (typeof ticket !== 'number' || ticket <= applied) return false;
      applied = ticket;
      return true;
    },
    // May this read lift the hold? Only when it was issued after the hold was set.
    mayLift(key, ticket) {
      return !(holds[key] > ticket);
    },
  };
}

// purchaseUntilFrom, for a read with a ticket: a read issued before the hold was set cannot lift it.
export function purchaseHoldFor(limits, guard, ticket, now = Date.now()) {
  const v = purchaseUntilFrom(limits, now);
  if (v === null && guard && !guard.mayLift('purchaseUntil', ticket)) return undefined;
  return v;
}

// Whether a pending card's Confirm / "Try again" button is off. A request in flight turns it off. At the daily
// report limit only a card the user never clicked Confirm on is off: one whose Confirm click was sent
// (`confirmClicked`) stays on, because the service lets that click take over its own stuck claim (PD-3).
// An error alone proves nothing: a failed decline, a 409 or a 404 set one without any Confirm click.
export function confirmDisabled(card, { busy = false, purchaseBlocked = false } = {}) {
  if (busy) return true;
  if (!purchaseBlocked || isActionCard(card)) return false; // the report limit holds purchases, never a My Data / Company Report action
  return !(card && card.confirmClicked);
}

// "at 3:42 PM" today, "tomorrow at 3:42 PM", or "on Mon, Oct 26 at 3:42 PM", in the browser's local time.
export function resetWhen(resetAt, now = Date.now()) {
  const d = new Date(resetAt);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(d) - day(new Date(now))) / 86400000);
  if (diff <= 0) return 'at ' + time;
  if (diff === 1) return 'tomorrow at ' + time;
  return 'on ' + d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) + ' at ' + time;
}

// The card that replaces the composer after a 429 daily_message_limit (or a counter at 0).
export function limitReachedView(resetAt, now = Date.now()) {
  const known = typeof resetAt === 'number' && isFinite(resetAt);
  return {
    title: 'Daily limit reached',
    text: known ? "You've reached today's limit, resets " + resetWhen(resetAt, now) + '.' : "You've reached today's limit. Please try again later.",
  };
}

// A refused message or click (a ChatApiError, by shape) -> {kind: 'message'|'purchase', resetAt (ms|null), cartUrl}
// or null. 429 daily_message_limit / daily_purchase_limit only; any other 429 is not a daily limit.
export function limitRefusal(e) {
  if (!e || e.status !== 429) return null;
  const kind = e.code === 'daily_message_limit' ? 'message' : e.code === 'daily_purchase_limit' ? 'purchase' : null;
  if (!kind) return null;
  return { kind, resetAt: parseResetAt(e.resetAt), cartUrl: typeof e.cartUrl === 'string' ? e.cartUrl : '' };
}

// The `purchase_limit` SSE event ({limit, resetAt, cartUrl, message}) -> the card of an answer.
// The conversation goes on; only buying a report in chat is blocked. Our copy, never the service's.
export function normalizePurchaseLimit(d, now = Date.now()) {
  if (!d || typeof d !== 'object') return null;
  const resetAt = parseResetAt(d.resetAt);
  return {
    key: 'purchase_limit',
    resetAt,
    cartUrl: typeof d.cartUrl === 'string' ? d.cartUrl : '',
    title: "Today's purchase limit is reached",
    text: purchaseLimitText(resetAt, now),
  };
}

// What a card or notice says when no more reports can be added from chat today.
export function purchaseLimitText(resetAt, now = Date.now()) {
  const until = typeof resetAt === 'number' && isFinite(resetAt) ? ', until ' + resetWhen(resetAt, now).replace(/^(at|on) /, '') : '';
  return 'You can keep chatting and ask about profiles and reports you already own. Adding more from chat is paused for today' + until + '.';
}

// A purchase click refused with 429 daily_purchase_limit: the card goes back to pending with this.
export function purchaseRefusedText(resetAt, now = Date.now()) {
  const when = typeof resetAt === 'number' && isFinite(resetAt) ? ' until ' + resetWhen(resetAt, now).replace(/^(at|on) /, '') : ' for today';
  return "Today's purchase limit is reached, so this can't be added from chat" + when + '. Nothing was charged.';
}

// ---------------------------------------------------------------------------
// Share with support (CHAT-08, PFD-220)
// ---------------------------------------------------------------------------

// What the confirm step says before the service has been asked (its `shareNote` only arrives with the
// share response or on a shared conversation). Matches the service's own note; once the service has sent
// its note, that text is shown instead.
export const SHARE_NOTE_FALLBACK = 'Bizminer support can read this conversation, including new messages, until you stop sharing.';
export const UNSHARE_NOTE = 'Bizminer support will no longer be able to read this conversation.';

// The confirm step of Share / Stop sharing.
export function shareDialog(mode, note) {
  if (mode === 'unshare') {
    return { title: 'Stop sharing with support?', text: UNSHARE_NOTE, confirmLabel: 'Stop sharing', busyLabel: 'Stopping…' };
  }
  return { title: 'Share with Bizminer support?', text: cleanDisplay(note, 400) || SHARE_NOTE_FALLBACK, confirmLabel: 'Share', busyLabel: 'Sharing…' };
}

// POST /conversations/:id/share -> {sharedAt, shareNote} (shareNote may be ''); null when there is no sharedAt.
// The same shape reads a GET /conversations/:id of a shared conversation.
export function normalizeShare(body) {
  if (!body || typeof body !== 'object' || typeof body.sharedAt !== 'string' || isNaN(new Date(body.sharedAt).getTime())) return null;
  return { sharedAt: body.sharedAt, shareNote: cleanDisplay(body.shareNote, 400) };
}

export const SHARE_FAILED_TEXT = "Couldn't share this conversation. Please try again.";
export const UNSHARE_FAILED_TEXT = "Couldn't stop sharing. Please try again.";
export const SHARE_GONE_TEXT = 'That conversation is no longer available.';

// A failed share or unshare (a ChatApiError, by shape) -> what to do.
//   gone          404: the conversation is gone
//   subscription  the subscription lapsed: the panel turns into the teaser
//   denied        not_in_beta / not_allowed: chat is hidden
//   failed        anything else (network, 5xx): try again
export function shareFailure(e, action) {
  const code = e && e.code;
  if (e && e.status === 404) return { kind: 'gone', text: SHARE_GONE_TEXT };
  if (isSubscriptionRequired(e)) return { kind: 'subscription', text: '' };
  if (code === 'not_in_beta' || code === 'not_allowed') return { kind: 'denied', text: '' };
  if (e && e.status === 0) return { kind: 'failed', text: "Couldn't reach Ask Bizminer. Please try again." };
  return { kind: 'failed', text: action === 'unshare' ? UNSHARE_FAILED_TEXT : SHARE_FAILED_TEXT };
}

// The history rows with one row's sharedAt set (null = not shared).
export function setSharedAt(items, id, sharedAt) {
  return (items || []).map((i) => (i.id === id ? { ...i, sharedAt: sharedAt || null } : i));
}

// The share state the open conversation reports (GET /conversations/:id), kept by id for a page whose list
// row may be hidden by a search. Returns the same map when nothing changed.
export function seedSharedMap(map, id, sharedAt) {
  if (id == null || id === '') return map || {};
  const base = map || {};
  const next = sharedAt || null;
  if (id in base && base[id] === next) return base;
  if (!(id in base) && next === null) return base;
  return { ...base, [id]: next };
}

// The share event the panel sends the page when it learns a conversation's state: {id, sharedAt (ISO|null), shareNote}.
export function shareStateEvent(id, conv) {
  const s = normalizeShare(conv);
  return { id: id == null ? null : id, sharedAt: s ? s.sharedAt : null, shareNote: s ? s.shareNote : '' };
}

const clip = (v, n = 200) => (v == null ? '' : String(v).trim().slice(0, n));

// Context for POST /conversations, built from a report header band.
export function buildReportContext(h) {
  if (!h || !h.industry) return null;
  const code = h.bznaics != null && h.bznaics !== '' ? parseFloat(h.bznaics) : null;
  const industry = (code && !isNaN(code) ? '[' + code + '] ' : '') + h.industry;
  const product = 'Industry Financial Profile';
  return {
    product,
    industry: clip(industry),
    area: clip(h.area),
    salesClass: clip(h.sales_class),
    label: clip([product, h.industry, h.area, h.sales_class].filter(Boolean).join(' · ')),
  };
}

export const contextKey = (c) => (c ? JSON.stringify([c.product, c.industry, c.area, c.salesClass]) : '');

// What an "Ask about this report" click does to the open conversation:
// 'none' (same report, or no report), 'reset' (a different report starts a new
// conversation) or 'refuse' (a purchase is in flight: resetting would abort its
// stream, so the panel keeps the conversation and says so).
export function contextSwitchAction({ next, current, working }) {
  if (!next || contextKey(next) === contextKey(current)) return 'none';
  return working ? 'refuse' : 'reset';
}

// ---------------------------------------------------------------------------
// Charts (CHAT-19)
// ---------------------------------------------------------------------------
// Every field of a chart is untrusted display data: values must be finite
// numbers, text is cleaned and bounded here, and it is only ever rendered as
// text (Vue interpolation) or, inside the one Apex tooltip that takes HTML,
// through escapeHtml.

export const CHART_LIMITS = { charts: 4, series: 8, points: 40, title: 120, unit: 24, label: 60, key: 300 };
export const CHART_DROPPED_NOTE = "Some values couldn't be verified and were left out.";

// One line of plain text: control characters out, whitespace collapsed, bounded.
function cleanDisplay(v, n) {
  if (v == null || typeof v === 'object') return '';
  return String(v)
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, n);
}

// The key a chart point uses to name its report: the same JSON the service
// computes for a citation of the same message.
export function chartCitationKey(c) {
  if (!c || typeof c !== 'object') return '';
  const part = (x, k) => (x && typeof x === 'object' ? x[k] : undefined);
  return JSON.stringify([c.product, part(c.industry, 'code'), part(c.area, 'id'), part(c.salesClass, 'id'), part(c.company, 'locnum')]);
}

// A `chart` event (or a history row) -> a chart the panel can draw, or null.
export function normalizeChart(d) {
  if (!d || typeof d !== 'object' || (d.kind !== 'bar' && d.kind !== 'line') || !Array.isArray(d.series)) return null;
  const series = [];
  for (const s of d.series) {
    if (series.length >= CHART_LIMITS.series) break;
    if (!s || typeof s !== 'object' || !Array.isArray(s.points)) continue;
    const points = [];
    for (const p of s.points) {
      if (points.length >= CHART_LIMITS.points) break;
      if (!p || typeof p !== 'object' || typeof p.value !== 'number' || !isFinite(p.value)) continue;
      const label = cleanDisplay(p.label, CHART_LIMITS.label);
      if (!label) continue;
      const key = p.source && typeof p.source.citationKey === 'string' ? p.source.citationKey.slice(0, CHART_LIMITS.key) : '';
      points.push({ label, value: p.value, citationKey: key, metric: cleanDisplay(p.metric, CHART_LIMITS.label), year: cleanDisplay(p.year, 12) });
    }
    if (points.length) series.push({ name: cleanDisplay(s.name, CHART_LIMITS.label) || 'Series ' + (series.length + 1), points });
  }
  if (!series.length) return null;
  const title = cleanDisplay(d.title, CHART_LIMITS.title);
  const dropped = typeof d.dropped === 'number' && isFinite(d.dropped) && d.dropped > 0 ? Math.floor(d.dropped) : 0;
  return {
    id: d.id != null && typeof d.id !== 'object' ? String(d.id).slice(0, 80) : title,
    kind: d.kind,
    title,
    unit: cleanDisplay(d.unit, CHART_LIMITS.unit),
    series,
    note: dropped > 0 ? CHART_DROPPED_NOTE : '',
  };
}

function normalizeCharts(list) {
  const out = [];
  (Array.isArray(list) ? list : []).forEach((raw) => {
    const c = normalizeChart(raw);
    if (c && out.length < CHART_LIMITS.charts && !out.some((x) => x.id === c.id)) out.push(c);
  });
  return out;
}

function groupDigits(n, maxDec) {
  return n.toLocaleString('en-US', { maximumFractionDigits: maxDec });
}

// 49.3 + '%' -> '49.3%', 1234567 + '$' -> '$1,234,567', 12.5 + 'days' -> '12.5 days'.
export function formatChartValue(value, unit) {
  if (typeof value !== 'number' || !isFinite(value)) return '';
  const u = unit == null ? '' : String(unit);
  const n = groupDigits(Math.abs(value), Math.abs(value) >= 1000 ? 0 : 2);
  const sign = value < 0 ? '-' : '';
  if (u === '$') return sign + '$' + n;
  if (u === '%') return sign + n + '%';
  return sign + n + (u ? ' ' + u : '');
}

// Short tick labels for the value axis: 1.5M, 42%, $2K.
export function formatAxisValue(value, unit) {
  if (typeof value !== 'number' || !isFinite(value)) return '';
  const a = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  let n;
  if (a >= 1e9) n = groupDigits(a / 1e9, 1) + 'B';
  else if (a >= 1e6) n = groupDigits(a / 1e6, 1) + 'M';
  else if (a >= 1e4) n = groupDigits(a / 1e3, 1) + 'K';
  else n = groupDigits(a, a >= 100 ? 0 : 1);
  if (unit === '$') return sign + '$' + n;
  if (unit === '%') return sign + n + '%';
  return sign + n;
}

// Two points are the same point only when report, metric and year all match (and,
// since rows written before metric/year existed carry neither, the label too).
const pointKey = (p) => JSON.stringify([p.citationKey, p.metric || '', p.year || '', p.label]);

// True when `part(p)` is filled in and different for every point in each group.
const tellsApart = (groups, part) => groups.every((g) => g.every((p) => part(p)) && new Set(g.map(part)).size === g.length);

const DASH = '\u2014';

// Display labels. A label that collides inside any one series is never dropped: every
// point carrying that label, in every series (so the series keep lining up), gets the
// same kind of suffix. The kind is chosen once for all series and labels (year when
// every colliding group is told apart by year, else metric, else both); a point that
// lacks the part shows an em-dash. Points that still collide (same label, metric and
// year from different reports) get a running number, shared across series: a point
// takes the slot of its own report's point when an earlier series has it, else the
// first slot it has not used, so the series stay aligned.
function uniqueLabels(series) {
  const bad = new Set();
  series.forEach((s) => {
    const seen = new Map();
    s.points.forEach((p) => seen.set(p.label, (seen.get(p.label) || 0) + 1));
    seen.forEach((n, label) => n > 1 && bad.add(label));
  });
  const collide = [];
  bad.forEach((label) => series.forEach((s) => {
    const ps = s.points.filter((p) => p.label === label);
    if (ps.length > 1) collide.push(ps);
  }));
  const mode = !collide.length ? '' : tellsApart(collide, (p) => p.year) ? 'year' : tellsApart(collide, (p) => p.metric) ? 'metric' : 'both';
  const suffixed = (p) => {
    if (mode === 'year') return p.label + ' ' + (p.year || DASH);
    if (mode === 'metric') return p.label + ' \u00b7 ' + (p.metric || DASH);
    return p.label + ' \u00b7 ' + (p.metric || DASH) + ' ' + (p.year || DASH);
  };
  const slots = new Map(); // suffixed label -> identities of the points that hold slot 0, 1, ...
  return series.map((s) => {
    const labels = s.points.map((p) => (bad.has(p.label) ? suffixed(p) : p.label));
    const claimed = new Map();
    const slotOf = s.points.map(() => -1);
    const claim = (i, label, slot) => {
      slotOf[i] = slot;
      claimed.set(label, (claimed.get(label) || new Set()).add(slot));
    };
    s.points.forEach((p, i) => {
      if (!bad.has(p.label)) return;
      const at = (slots.get(labels[i]) || []).indexOf(pointKey(p));
      if (at !== -1 && !(claimed.get(labels[i]) || new Set()).has(at)) claim(i, labels[i], at);
    });
    s.points.forEach((p, i) => {
      if (!bad.has(p.label) || slotOf[i] !== -1) return;
      const ids = slots.get(labels[i]) || [];
      slots.set(labels[i], ids);
      const used = claimed.get(labels[i]) || new Set();
      let at = ids.findIndex((_, j) => !used.has(j));
      if (at === -1) at = ids.push(pointKey(p)) - 1;
      claim(i, labels[i], at);
    });
    return labels.map((label, i) => (slotOf[i] > 0 ? label + ' (' + (slotOf[i] + 1) + ')' : label));
  });
}

// What the chart draws: shared categories (the union of the series' points, by
// display label, in order of first appearance), one data array per series (null
// where a series has no point), and for every point its text and the label of
// the report it came from (the citation of the same message whose key matches;
// '' when none does).
export function chartModel(chart, citations) {
  const labels = new Map();
  (citations || []).forEach((c) => {
    const k = chartCitationKey(c);
    if (k && !labels.has(k)) labels.set(k, citationLabel(c));
  });
  // Exact duplicates (same report, metric and year) are the only points dropped.
  const deduped = chart.series.map((s) => {
    const seen = new Set();
    return { name: s.name, points: s.points.filter((p) => !seen.has(pointKey(p)) && seen.add(pointKey(p))) };
  });
  const display = uniqueLabels(deduped);
  const categories = [];
  display.forEach((ls) => ls.forEach((l) => categories.indexOf(l) === -1 && categories.push(l)));
  const sources = [];
  const series = deduped.map((s, si) => {
    const byLabel = new Map();
    s.points.forEach((p, pi) => {
      const label = display[si][pi];
      const source = labels.get(p.citationKey) || '';
      if (source && sources.indexOf(source) === -1) sources.push(source);
      byLabel.set(label, { label, value: p.value, text: formatChartValue(p.value, chart.unit), source });
    });
    const points = categories.map((l) => byLabel.get(l) || null);
    return { name: s.name, points, data: points.map((p) => (p ? p.value : null)) };
  });
  return {
    kind: chart.kind,
    unit: chart.unit,
    categories,
    series,
    sources,
    // Many or long labels do not fit a 400px column under the bars: lay them out as rows.
    horizontal: chart.kind === 'bar' && (categories.length > 5 || categories.some((l) => l.length > 14)),
  };
}

// The one place Apex takes HTML (its custom tooltip): everything is escaped here.
export function chartTooltipHtml(model, seriesIndex, pointIndex) {
  const s = model && model.series[seriesIndex];
  const p = s && s.points[pointIndex];
  if (!p) return '';
  return (
    '<div class="bzc-tt">' +
    (s.name ? '<div class="bzc-tt-series">' + escapeHtml(s.name) + '</div>' : '') +
    '<div class="bzc-tt-label">' + escapeHtml(p.label) + '</div>' +
    '<div class="bzc-tt-value">' + escapeHtml(p.text) + '</div>' +
    (p.source ? '<div class="bzc-tt-src">Source: ' + escapeHtml(p.source) + '</div>' : '') +
    '</div>'
  );
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function initialChatState() {
  return { conversationId: null, messages: [], busy: false };
}

function patch(state, id, fn) {
  return {
    ...state,
    messages: state.messages.map((m) => (m.id === id ? fn(m) : m)),
  };
}

// Where a Confirm card stands. Client states: pending | buying | declining |
// owned (purchased) | declined | expired | rejected | redeeming. Server states
// (GET /conversations/:id `confirmations[].status` and 409 confirmation_closed):
// pending | redeeming | redeemed | declined | expired.
const NO_CHARGE = {
  declined: 'Nothing was charged.',
  expired: 'This offer expired and nothing was charged.',
  rejected: 'That offer was no longer valid and nothing was charged.',
};
// Used only when the server's message is empty. They claim "nothing was charged"
// only where the server said charged:false (closedMessage).
const CLOSED = {
  declined: 'Not purchased.',
  expired: 'This offer expired.',
  rejected: 'That offer was no longer valid.',
};
const PURCHASED = 'Added to your profiles and reports.';
const ALREADY_OWNED = 'You already own this profile or report.';
const NOT_COVERED_CARD = "This profile or report isn't included in your plan.";
const IN_FLIGHT = 'This purchase is still being processed. Check your profiles and reports in a moment, or ask again for a fresh offer.';
const STALLED = 'This purchase is taking longer than usual.';
const SUB_NOTE = "This uses your team's subscription profiles and reports.";
const CHARGE_FALLBACK = "Uses 1 of your subscription's profiles and reports";

// Non-purchase Confirm cards (chat.bizminer.com ConfirmCard.action). Such a card is not a purchase: its `prompt` is
// the whole text, there is no charge line, and it has its own buttons and words. ONE TABLE: change the copy here.
//   confirm/cancel  button labels      busy      the label while the click is working
//   destructive     red Confirm button doneStatus the card state once it went through (outcome and server `redeemed`)
//   done            the settled line   noChange   what "nothing was ..." says on an expired offer
//   headlines       the card's header  inFlight/stalled/error  the text of a click still in flight / taking long / unclear
//   promptFallback  when the card sent neither prompt nor chargeText
export const CARD_ACTIONS = {
  delete_my_data: {
    confirm: 'Delete',
    cancel: 'Cancel',
    busy: 'Deleting…',
    destructive: true,
    doneStatus: 'deleted',
    done: 'Deleted.',
    noChange: 'deleted',
    headlines: { pending: 'Delete My Data set', done: 'My Data set deleted', declined: 'Not deleted', progress: 'Deletion in progress' },
    inFlight: 'This deletion is still being processed. Check your My Data in a moment.',
    stalled: 'This deletion is taking longer than usual.',
    error: "We couldn't confirm that the My Data set was deleted. Check your My Data before trying again.",
    expiredNote: 'This offer has timed out. Ask again to delete it.',
    promptFallback: "Delete this My Data set? This can't be undone.",
  },
  overwrite_my_data: {
    confirm: 'Replace',
    cancel: 'Cancel',
    busy: 'Saving…',
    destructive: false,
    doneStatus: 'saved',
    done: 'Saved.',
    noChange: 'changed',
    headlines: { pending: 'Replace My Data set', done: 'My Data set saved', declined: 'Not changed', progress: 'Save in progress' },
    inFlight: 'This save is still being processed. Check your My Data in a moment.',
    stalled: 'This save is taking longer than usual.',
    error: "We couldn't confirm that your My Data was saved. Check your My Data before trying again.",
    expiredNote: 'This offer has timed out. Ask again to save it.',
    promptFallback: 'Replace all years of this My Data set with the figures above?',
  },
  update_company_report: {
    confirm: 'Replace',
    cancel: 'Cancel',
    busy: 'Saving…',
    destructive: false,
    doneStatus: 'saved',
    done: 'Saved.',
    noChange: 'changed',
    headlines: { pending: 'Update Company Report', done: 'Company Report updated', declined: 'Not changed', progress: 'Save in progress' },
    inFlight: 'This save is still being processed. Check your Company Report in a moment.',
    stalled: 'This save is taking longer than usual.',
    error: "We couldn't confirm that the Company Report was saved. Check it before trying again.",
    expiredNote: 'This offer has timed out. Ask again to save it.',
    promptFallback: 'Update the saved inputs of this Company Report?',
  },
};
const CANCELLED = 'Cancelled.';

// The table row for an action name, or null (no action, or one this panel does not know: shown as a purchase card).
export function cardActionCopy(action) {
  return typeof action === 'string' && Object.prototype.hasOwnProperty.call(CARD_ACTIONS, action) ? CARD_ACTIONS[action] : null;
}
// The same for a card; null for a purchase card.
export const cardCopy = (c) => (c ? cardActionCopy(c.action) : null);
export const isActionCard = (c) => cardCopy(c) !== null;

// The card's header line for a non-purchase card.
export function actionHeadline(c) {
  const t = cardCopy(c);
  if (!t) return '';
  const h = t.headlines;
  return { deleted: h.done, saved: h.done, declined: h.declined, expired: 'Offer expired', rejected: 'Offer expired', redeeming: h.progress, stalled: h.progress }[c.status] || h.pending;
}

function noChangeText(t, outcome) {
  return outcome === 'rejected' ? `That offer was no longer valid and nothing was ${t.noChange}.` : `This offer expired and nothing was ${t.noChange}.`;
}

function closedMessage(outcome, charged, card) {
  const t = cardCopy(card);
  if (t) {
    if (outcome === 'declined') return CANCELLED;
    if (outcome === 'expired' || outcome === 'rejected') return noChangeText(t, outcome);
  }
  if (outcome === 'deleted') return 'Deleted.';
  if (outcome === 'saved') return 'Saved.';
  if (outcome === 'purchased') return PURCHASED;
  if (outcome === 'already_owned') return ALREADY_OWNED;
  if (outcome === 'not_covered') return NOT_COVERED_CARD;
  return charged === false ? NO_CHARGE[outcome] : CLOSED[outcome];
}

// {status, charged, message} for a server-side card state. `charged` is true or
// false only where the server's state settles it; null = unknown (never claim
// "nothing was charged" for it). A redeemed card was bought with a subscription
// report, so there is no charge to claim.
export function confirmCardPatch(serverStatus, cardAction) {
  const t = cardActionCopy(cardAction);
  if (t) {
    switch (serverStatus) {
      case 'pending':
        return { status: 'pending', charged: null, message: '' };
      case 'redeemed':
        return { status: t.doneStatus, charged: false, message: t.done };
      case 'declined':
        return { status: 'declined', charged: false, message: CANCELLED };
      case 'expired':
        return { status: 'expired', charged: false, message: noChangeText(t, 'expired') };
      default: // redeeming, or a state this panel does not know
        return { status: 'redeeming', charged: null, message: t.inFlight };
    }
  }
  switch (serverStatus) {
    case 'pending':
      return { status: 'pending', charged: null, message: '' };
    case 'redeemed':
      return { status: 'owned', charged: null, message: PURCHASED };
    case 'declined':
      return { status: 'declined', charged: false, message: NO_CHARGE.declined };
    case 'expired':
      return { status: 'expired', charged: false, message: NO_CHARGE.expired };
    default: // redeeming, or a state this panel does not know
      return { status: 'redeeming', charged: null, message: IN_FLIGHT };
  }
}

// The text under a card that went back to pending after a failed click. Uses the
// server's user-safe message when there is one; the fallback never claims that
// nothing was charged (only a confirm_result with charged:false may say that).
// `cardAction` is the card's own action (see CARD_ACTIONS), not the click's.
export function purchaseErrorText(error, action, cardAction) {
  if (error && typeof error.message === 'string' && error.message.trim() !== '') return error.message.trim();
  if (action === 'decline') return "We couldn't cancel that. Please try again.";
  const t = cardActionCopy(cardAction);
  if (t) return t.error;
  return "We couldn't confirm that purchase. Check your profiles and reports before trying again; you won't be charged twice for the same one.";
}

function remainingOf(sub) {
  const r = sub && sub.remaining;
  return typeof r === 'number' && isFinite(r) ? r : null;
}

// "Uses 1 of your N remaining subscription reports", as the server wrote it.
export function chargeLine(c) {
  if (isActionCard(c)) return ''; // not a purchase: nothing is charged
  return (c && typeof c.chargeText === 'string' && c.chargeText.trim()) || CHARGE_FALLBACK;
}

// The note for a sub-user (the team's subscription pays): the server's text, or
// the same words for an older card that carries only the flag.
export function subUserNoteLine(c, panelSubUser) {
  if (isActionCard(c)) return '';
  const on = c && typeof c.subUserNote === 'boolean' ? c.subUserNote : !!panelSubUser;
  return on ? (c && c.subUserNoteText) || SUB_NOTE : '';
}

// The real Confirm card (chat.bizminer.com src/chat/confirm.ts ConfirmCard).
// Display strings are derived here; chargeText / subUserNote / expiresAt are kept
// as the server sent them.
export function normalizeConfirm(d, serverStatus = 'pending') {
  if (!d || typeof d !== 'object' || d.pendingId == null || d.pendingId === '') return null;
  const t = cardActionCopy(d.action);
  const patch = confirmCardPatch(serverStatus, t ? d.action : undefined);
  // A non-purchase card's text is its prompt (chargeText repeats it, so it is kept off the card: no duplicate, no charge line).
  const prompt = t ? cleanDisplay(d.prompt, 300) || cleanDisplay(d.chargeText, 300) || t.promptFallback : '';
  return {
    ...(t ? { action: d.action, prompt } : {}),
    pendingId: String(d.pendingId),
    product: nameOf(d.product) || 'Profile or report',
    industry: industryOf(d.industry),
    area: nameOf(d.area),
    salesClass: nameOf(d.salesClass),
    company: companyOf(d.company),
    chargeText: !t && typeof d.chargeText === 'string' ? d.chargeText : '',
    subscriptionRemaining: remainingOf(d.subscription),
    expiresAt: d.expiresAt || null,
    subUserNote: typeof d.subUserNote === 'boolean' ? d.subUserNote : null,
    subUserNoteText: typeof d.subUserNoteText === 'string' && d.subUserNoteText.trim() ? d.subUserNoteText.trim() : null,
    status: patch.status,
    charged: patch.charged,
    message: patch.message,
    reoffered: false, // a click closed this offer and a new card arrived in the same stream
    error: null,
    // The user's Confirm click was sent. From history only the server's state shows it: a claim in flight
    // (`redeeming`) or a redeemed card came from a click; a `pending` card reads as never clicked.
    confirmClicked: serverStatus === 'redeeming' || serverStatus === 'redeemed',
  };
}

const OUTCOME_STATUS = {
  purchased: 'owned',
  already_owned: 'already_owned',
  not_covered: 'not_covered',
  declined: 'declined',
  expired: 'expired',
  rejected: 'rejected',
  deleted: 'deleted',
  saved: 'saved',
};

// "Show a new offer" is for a card that closed (expired or rejected) with no
// newer card; never for redeeming (a purchase in flight), where the state is
// polled instead.
export function canOfferNewOffer(c) {
  return !!c && !isActionCard(c) && (c.status === 'expired' || c.status === 'rejected') && !c.reoffered;
}

// What a click on a card did, from its confirm stream: one confirm_result, or an
// error with no result (the card goes back to pending), or nothing usable.
export function createConfirmTracker(clickedId) {
  let result = null;
  let error = null;
  let done = false;
  let newCard = false; // a confirm card for a DIFFERENT pendingId arrived in this stream
  const clicked = clickedId == null ? null : String(clickedId);
  return {
    // 'result'  consumed: update the card
    // 'held'    consumed: shown on the card, not in the transcript
    // 'show'    belongs in the continuing answer
    see(name, data) {
      data = data || {};
      if (name === 'confirm_result') {
        if (!OUTCOME_STATUS[data.outcome] || data.pendingId == null) return 'held';
        result = { pendingId: String(data.pendingId), outcome: data.outcome, charged: data.charged === true, message: String(data.message || '') };
        return 'result';
      }
      if (name === 'confirm' && data.pendingId != null && String(data.pendingId) !== clicked) newCard = true;
      if (name === 'error' && !result) {
        error = { code: String(data.code || 'internal_error'), message: String(data.message || ''), retryable: data.retryable !== false };
        return 'held';
      }
      if (name === 'done') {
        done = true;
        return error ? 'held' : 'show';
      }
      return 'show';
    },
    verdict() {
      // F4: the closed card is "re-offered" only when a new card really arrived
      // in the same stream, known for sure at done.
      if (result) return { kind: 'result', result, done, reoffered: done && newCard && (result.outcome === 'expired' || result.outcome === 'rejected') };
      if (error) return { kind: 'error', error, done };
      return { kind: done ? 'unknown' : 'dropped', done };
    },
  };
}

function patchCard(state, pendingId, fn) {
  const id = String(pendingId);
  return {
    ...state,
    messages: state.messages.map((m) =>
      m.confirms && m.confirms.some((c) => c.pendingId === id)
        ? { ...m, confirms: m.confirms.map((c) => (c.pendingId === id ? fn(c) : c)) }
        : m
    ),
  };
}

// D3: copy the server's state onto the cards that are still redeeming here, by
// pendingId. Nothing else changes: not the messages, not the order, not busy.
function patchRedeemingCards(state, messages) {
  const server = new Map();
  (messages || []).forEach((m) => (m.confirms || []).forEach((c) => server.set(c.pendingId, c)));
  if (!server.size) return state;
  return {
    ...state,
    messages: state.messages.map((m) =>
      (m.confirms || []).some((c) => c.status === 'redeeming' && server.has(c.pendingId))
        ? {
            ...m,
            confirms: m.confirms.map((c) => {
              const s = c.status === 'redeeming' ? server.get(c.pendingId) : null;
              return s && s.status !== 'redeeming'
                ? { ...c, status: s.status, charged: s.charged, message: s.message, subscriptionRemaining: s.subscriptionRemaining != null ? s.subscriptionRemaining : c.subscriptionRemaining, error: null }
                : c;
            }),
          }
        : m
    ),
  };
}

function applyConfirmResult(state, data) {
  if (!data || data.pendingId == null || !OUTCOME_STATUS[data.outcome]) return state;
  const status = OUTCOME_STATUS[data.outcome];
  // true / false as the server said; null (or absent) = unknown, never "nothing was charged".
  const charged = data.charged === true ? true : data.charged === false ? false : null;
  const remaining = remainingOf(data.subscription);
  return patchCard(state, data.pendingId, (c) => ({
    ...c,
    status,
    charged,
    // A non-purchase card says its own fixed words once settled; every other card says the server's.
    message: ['deleted', 'saved', 'declined'].includes(data.outcome) && isActionCard(c) ? closedMessage(data.outcome, charged, c) : String(data.message || '') || closedMessage(data.outcome, charged, c),
    subscriptionRemaining: remaining != null ? remaining : c.subscriptionRemaining,
    reoffered: false, // set by the 'reoffer' action once a new card has really arrived
    error: null,
  }));
}

// CHAT-18/c. A report the subscription does not cover: no offer, a contact path.
// PFD-235: `buy_in_app` is a Prospect List purchase attempt; chat never buys one, the card sends the customer to the app.
const NOT_COVERED_TEXT = {
  no_reports_left: "You've used all your subscription's profiles and reports",
  not_in_plan: "Your plan doesn't include this profile or report",
  buy_in_app: 'Prospect lists are bought in the app',
};
export function normalizeNotCovered(d) {
  if (!d || typeof d !== 'object') return null;
  const reason = NOT_COVERED_TEXT[d.reason] ? d.reason : 'not_in_plan';
  const combo = d.combination && typeof d.combination === 'object' ? d.combination : {};
  const key = JSON.stringify([reason, combo.product, combo.industry, combo.area, combo.salesClass, combo.company]);
  if (reason === 'buy_in_app') {
    // No contact path and no price: only the app link (checked with safeAppLink where it is drawn).
    return {
      key,
      reason,
      title: NOT_COVERED_TEXT[reason],
      text: 'Pick your filters and buy the list on the Prospect List page.',
      label: citationLabel(combo),
      contactUrl: '',
      buyUrl: typeof d.buyUrl === 'string' ? d.buyUrl : '',
    };
  }
  return {
    key,
    reason,
    title: 'Not included in your plan',
    text: NOT_COVERED_TEXT[reason],
    label: citationLabel(combo),
    contactUrl: typeof d.contactUrl === 'string' ? d.contactUrl : '',
  };
}

// ---------------------------------------------------------------------------
// Prospects preview (PFD-235). The free count of a filter set, or the rows of a list the account already owns.
// Nothing is bought in chat and no price is shown: every field is rebuilt from an allow-list, so a price, a quote,
// credits, contact details or any unknown field in a stored or streamed event never reaches the card.
// ---------------------------------------------------------------------------

export const PROSPECT_LIMITS = { rows: 10, perTurn: 3 };
const PROSPECT_SOURCES = ['count', 'owned_list'];
const PROSPECT_ENUM_FILTERS = {
  franchise: ['any', 'only', 'exclude'],
  web_presence: ['any', 'has', 'none'],
  social_presence: ['any', 'has', 'none'],
};
const PROSPECT_NUMBER_FILTERS = ['revenue_from', 'revenue_to', 'value_from', 'value_to', 'employees_min', 'employees_max', 'years_in_business_min'];
const PROSPECT_FLAG_FILTERS = ['require_phone', 'require_contact_name', 'require_street_address'];

const isPlainObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
function prospectText(v, max) {
  if (typeof v !== 'string' && typeof v !== 'number') return null;
  // eslint-disable-next-line no-control-regex
  const t = String(v).replace(/[\u0000-\u001f\u007f<>]/g, ' ').replace(/\s+/g, ' ').trim();
  if (t === '') return null;
  return t.length > max ? t.slice(0, max - 1) + '\u2026' : t;
}
const prospectCount = (v) => (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 ? v : null);

// One row: the fields the card shows (and the contract's address/website/zip), never a price or value.
export function normalizeProspectRow(r) {
  if (!isPlainObj(r)) return null;
  const row = {
    bizid: typeof r.bizid === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(r.bizid) ? r.bizid : null,
    name: prospectText(r.name, 120),
    address: prospectText(r.address, 120),
    city: prospectText(r.city, 60),
    state: prospectText(r.state, 32),
    zip: prospectText(r.zip, 10),
    website: prospectText(r.website, 200),
    employees: prospectCount(r.employees),
    salesRange: prospectText(r.salesRange, 40),
  };
  return row.name === null && row.bizid === null ? null : row;
}

function prospectFilters(raw) {
  const out = {};
  if (!isPlainObj(raw)) return out;
  PROSPECT_NUMBER_FILTERS.forEach((k) => {
    const n = prospectCount(raw[k]);
    if (n !== null) out[k] = n;
  });
  PROSPECT_FLAG_FILTERS.forEach((k) => {
    if (typeof raw[k] === 'boolean') out[k] = raw[k];
  });
  Object.keys(PROSPECT_ENUM_FILTERS).forEach((k) => {
    if (typeof raw[k] === 'string' && PROSPECT_ENUM_FILTERS[k].indexOf(raw[k]) !== -1) out[k] = raw[k];
  });
  return out;
}

export function normalizeProspects(d) {
  if (!isPlainObj(d) || PROSPECT_SOURCES.indexOf(d.source) === -1) return null;
  const count = prospectCount(d.count);
  if (count === null) return null;
  const c = isPlainObj(d.combination) ? d.combination : {};
  const ind = isPlainObj(c.industry) ? c.industry : null;
  const are = isPlainObj(c.area) ? c.area : null;
  const code = ind ? prospectText(ind.code, 16) : null;
  const areaId = are ? prospectCount(are.id) : null;
  const owned = d.source === 'owned_list';
  const rows = owned && Array.isArray(d.rows) ? d.rows.slice(0, PROSPECT_LIMITS.rows).map(normalizeProspectRow).filter(Boolean) : [];
  const p = {
    source: d.source,
    count,
    rows,
    combination: {
      product: 'Prospect List',
      industry: code !== null && /^\d{2,8}(\.\d+)?$/.test(code) ? { code, name: prospectText(ind.name, 120) } : null,
      area: areaId !== null ? { id: areaId, name: prospectText(are.name, 120) } : null,
      zipCount: prospectCount(c.zipCount) || null,
      listName: owned ? prospectText(c.listName, 120) : null,
    },
    filters: prospectFilters(d.filters),
    buyUrl: typeof d.buyUrl === 'string' ? d.buyUrl.slice(0, 500) : '',
  };
  p.key = JSON.stringify([p.source, p.count, p.combination, p.filters, p.rows.map((x) => x.bizid || x.name)]);
  return p;
}

// A stored `prospects` array from history: the same allow-list, at most 3, no repeats.
export function normalizeProspectsList(list) {
  const out = [];
  (Array.isArray(list) ? list : []).forEach((x) => {
    const p = normalizeProspects(x);
    if (p && out.length < PROSPECT_LIMITS.perTurn && !out.some((y) => y.key === p.key)) out.push(p);
  });
  return out;
}

const FILTER_LABELS = {
  revenue_from: ['Annual sales from', 'n'],
  revenue_to: ['Annual sales up to', 'n'],
  value_from: ['Estimated value from', 'n'],
  value_to: ['Estimated value up to', 'n'],
  employees_min: ['At least', 'employees'],
  employees_max: ['At most', 'employees'],
  years_in_business_min: ['In business at least', 'years'],
  require_phone: ['Has a phone number'],
  require_contact_name: ['Has a contact name'],
  require_street_address: ['Has a street address'],
};
const ENUM_LABELS = {
  franchise: { only: 'Franchises only', exclude: 'Franchises excluded' },
  web_presence: { has: 'Has a website', none: 'No website' },
  social_presence: { has: 'Has social media', none: 'No social media' },
};

// The filters as short lines of plain text for the card (a plain number, no currency sign: chat shows no price).
export function prospectFilterLines(filters) {
  const f = isPlainObj(filters) ? filters : {};
  const out = [];
  PROSPECT_NUMBER_FILTERS.forEach((k) => {
    if (typeof f[k] !== 'number') return;
    const [lead, unit] = FILTER_LABELS[k];
    out.push(unit === 'n' ? lead + ' ' + f[k].toLocaleString('en-US') : lead + ' ' + f[k].toLocaleString('en-US') + ' ' + unit);
  });
  PROSPECT_FLAG_FILTERS.forEach((k) => {
    if (f[k] === true) out.push(FILTER_LABELS[k][0]);
  });
  Object.keys(ENUM_LABELS).forEach((k) => {
    if (typeof f[k] === 'string' && ENUM_LABELS[k][f[k]]) out.push(ENUM_LABELS[k][f[k]]);
  });
  return out;
}

function applyEvent(m, name, data) {
  data = data || {};
  switch (name) {
    case 'step': {
      if (data.id == null) return m;
      const step = {
        id: String(data.id),
        label: String(data.label || ''),
        state: data.state === 'done' ? 'done' : 'running',
      };
      const i = m.steps.findIndex((s) => s.id === step.id);
      const steps = m.steps.slice();
      if (i === -1) steps.push(step);
      else steps[i] = { ...steps[i], ...step, label: step.label || steps[i].label };
      return { ...m, steps };
    }
    case 'text':
      return { ...m, text: m.text + (typeof data.delta === 'string' ? data.delta : '') };
    case 'citation': {
      const key = JSON.stringify([data.product, data.industry, data.area, data.salesClass, data.company]);
      if (m.citations.some((c) => c._key === key)) return m;
      return { ...m, citations: m.citations.concat([{ ...data, _key: key }]) };
    }
    case 'chart': {
      const c = normalizeChart(data);
      if (!c || m.charts.length >= CHART_LIMITS.charts || m.charts.some((x) => x.id === c.id)) return m;
      return { ...m, charts: m.charts.concat([c]) };
    }
    // A refusal after text or a citation went out: the decline text follows as ordinary `text`.
    case 'text_reset':
      return { ...m, text: '', citations: [], charts: [], prospects: [], ownedFree: false };
    case 'ownership':
      return { ...m, ownedFree: data.owned === true && data.charged === false };
    case 'confirm': {
      const c = normalizeConfirm(data);
      if (!c || m.confirms.some((x) => x.pendingId === c.pendingId)) return m;
      return { ...m, confirms: m.confirms.concat([c]) };
    }
    case 'not_covered': {
      const n = normalizeNotCovered(data);
      if (!n || m.notCovered.some((x) => x.key === n.key)) return m;
      return { ...m, notCovered: m.notCovered.concat([n]) };
    }
    // PFD-235: the free count (or the rows of an owned list); at most 3 per turn, none repeated.
    case 'prospects': {
      const p = normalizeProspects(data);
      if (!p || m.prospects.length >= PROSPECT_LIMITS.perTurn || m.prospects.some((x) => x.key === p.key)) return m;
      return { ...m, prospects: m.prospects.concat([p]) };
    }
    // CHAT-06/b: today's report limit is reached; no Confirm card follows, the answer goes on.
    case 'purchase_limit':
      return { ...m, purchaseLimit: normalizePurchaseLimit(data) || m.purchaseLimit };
    // CHAT-11/a: the financial-data notice, before the first input form.
    case 'notice':
      return { ...m, notice: normalizeNotice(data) || m.notice };
    // CHAT-11/b: an input form for the customer's own figures (the turn ends on it).
    case 'form': {
      const f = normalizeForm(data);
      if (!f || m.forms.some((x) => x.id === f.id)) return m;
      return { ...m, forms: m.forms.concat([f]), notice: f.noticeRequired && !m.notice ? loadingNotice() : m.notice };
    }
    case 'error':
      return {
        ...m,
        status: 'error',
        error: {
          code: String(data.code || 'internal_error'),
          message: String(data.message || 'Something went wrong. You can try again.'),
          retryable: data.retryable !== false,
        },
      };
    case 'done':
      return finish(m, data.messageId);
    default:
      return m;
  }
}

const INTERRUPTED = {
  code: 'interrupted',
  message: 'This answer was interrupted. Ask again to retry.',
  retryable: true,
};

function finish(m, serverId) {
  return {
    ...m,
    serverId: serverId != null ? serverId : m.serverId,
    status: m.status === 'error' ? 'error' : 'complete',
    steps: m.steps.map((s) => (s.state === 'running' ? { ...s, state: 'done' } : s)),
  };
}

// formRef: the id of the input form whose submission this answer belongs to
// (null for an answer to a question), so a failed submission can be retried.
export function newAssistant(id, formRef) {
  return {
    id,
    role: 'assistant',
    text: '',
    steps: [],
    citations: [],
    charts: [],
    ownedFree: false,
    confirms: [],
    notCovered: [],
    prospects: [],
    purchaseLimit: null,
    notice: null,
    forms: [],
    formRef: formRef || null,
    error: null,
    status: 'streaming',
  };
}

export function chatReducer(state, action) {
  switch (action.type) {
    case 'reset':
      return initialChatState();
    case 'conversation':
      return { ...state, conversationId: action.conversationId };
    case 'history':
      return { ...state, conversationId: action.conversationId, busy: false, messages: action.messages };
    case 'user':
      return {
        ...state,
        busy: true,
        messages: state.messages.concat([{ id: action.id, role: 'user', text: action.text }]),
      };
    case 'assistant':
      return { ...state, messages: state.messages.concat([newAssistant(action.id, action.formRef)]) };
    case 'event': {
      // The outcome of a click lands on the card (an earlier message), not on
      // the answer that streams after it. The same for a form submission.
      if (action.name === 'confirm_result') return applyConfirmResult(state, action.data);
      if (action.name === 'form_result') return applyFormResult(state, action.data);
      const next = patch(state, action.id, (m) => applyEvent(m, action.name, action.data));
      return action.name === 'done' ? { ...next, busy: false } : next;
    }
    // Transport failure (network drop, 4xx/5xx before or during the stream).
    case 'fail':
      return {
        ...patch(state, action.id, (m) =>
          finish(
            {
              ...m,
              status: 'error',
              error: {
                code: action.code || 'connection',
                message: action.message || 'The connection was interrupted. You can try again.',
                retryable: action.retryable !== false,
              },
            },
            null
          )
        ),
        busy: false,
      };
    // The stream ended without a `done` frame: an answer still streaming was cut
    // off. Keep what arrived, mark it interrupted and offer Retry.
    case 'streamEnd':
      return {
        ...patch(state, action.id, (m) =>
          m.status === 'streaming'
            ? finish({ ...m, status: 'error', error: { ...INTERRUPTED } }, null)
            : m
        ),
        busy: false,
      };
    case 'drop':
      return { ...state, messages: state.messages.filter((m) => m.id !== action.id) };
    // A new confirm card really arrived after this closed one (F4).
    case 'reoffer':
      return patchCard(state, action.pendingId, (c) => ({ ...c, reoffered: true }));
    // Polling gave up (F1/F2): an answer still "answering" is shown as interrupted.
    // P3: slow polling gave up too: a redeeming card stops spinning and offers
    // "Check again". Sending a message stays possible (nothing is busy).
    case 'settleStalled':
      return {
        ...state,
        messages: state.messages.map((m) =>
          (m.confirms || []).some((c) => c.status === 'redeeming')
            ? { ...m, confirms: m.confirms.map((c) => (c.status === 'redeeming' ? { ...c, status: 'stalled', charged: null, message: isActionCard(c) ? cardCopy(c).stalled : STALLED } : c)) }
            : m
        ),
      };
    case 'settleTimeout':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.status === 'answering'
            ? { ...m, status: 'error', error: { code: 'still_answering', message: 'This answer is taking longer than expected. Check back in a moment, or ask again.', retryable: true } }
            : m
        ),
      };
    // D3: a background poll result (GET /conversations/:id). While a turn streams,
    // or when the request went out before the turn started (`patchOnly`), it may
    // only update the redeeming cards; the message list is never replaced.
    case 'poll':
      return state.busy || action.patchOnly
        ? patchRedeemingCards(state, action.messages)
        : chatReducer(state, { ...action, type: 'history', conversationId: action.conversationId || state.conversationId });
    // CHAT-11: the notice and the input forms.
    case 'formPhase':
      // A new submission starts with no result, so the next `saved` is a change the card can see (it clears the draft).
      return patchForm(state, action.formId, (f) => (action.phase === 'submitting' ? { ...f, phase: 'submitting', result: null } : { ...f, phase: 'editing' }));
    case 'formInvalid':
      return patchForm(state, action.formId, (f) => ({ ...f, phase: 'editing', result: 'rejected', errors: normalizeFieldErrors(action.fields), message: FORM_CHECK_MESSAGE }));
    case 'formFailed':
      return patchForm(state, action.formId, (f) => ({ ...f, phase: 'editing', result: 'error', message: cleanDisplay(action.message, 500) }));
    case 'noticeAcking':
      return mapNotices(state, (n) => (n.status === 'open' ? { ...n, status: 'acking', error: null } : n));
    case 'noticeFailed':
      return mapNotices(state, (n) => (n.status === 'acking' ? { ...n, status: 'open', error: cleanDisplay(action.message, 300) || NOTICE_ACK_FAILED } : n));
    case 'noticeAcked':
      return unlockForms(mapNotices(state, (n) => ({ ...n, status: 'acked', error: null })));
    case 'noticeRequired':
      return action.pendingId != null ? noticeRequiredForCard(state, action.pendingId) : noticeRequiredFor(state, action.formId);
    case 'noticeLoaded':
      return noticeLoaded(state, action.notice);
    case 'noticeLoadFailed':
      return mapNotices(state, (n) => (n.status === 'loading' ? { ...n, status: 'failed', error: cleanDisplay(action.message, 300) || NOTICE_LOAD_FAILED } : n));
    case 'noticeLoading':
      return mapNotices(state, (n) => (n.status === 'failed' || n.status === 'open' ? { ...n, status: 'loading', error: null } : n));
    case 'confirmState':
      return patchCard(state, action.pendingId, (c) => {
        const next = { ...c, status: action.status, error: action.error || null };
        if (action.confirmClicked !== undefined) next.confirmClicked = !!action.confirmClicked;
        if (action.charged !== undefined) next.charged = action.charged;
        if (action.message !== undefined) next.message = action.message;
        if (action.reoffered !== undefined) next.reoffered = action.reoffered;
        return next;
      });
    default:
      return state;
  }
}

// GET /conversations/:id -> reducer messages. `confirmation_outcome` rows are
// the app's record of a click (never shown); each assistant row carries the
// Confirm cards it ended on, with their current state, so a pending card
// survives a reload.
export function messagesFromHistory(rows) {
  const out = [];
  (rows || []).forEach((r) => {
    if ((r.kind || 'message') !== 'message') return;
    if (r.role === 'user') {
      out.push({ id: 'h' + r.id, role: 'user', text: String(r.text || '') });
      return;
    }
    const m = newAssistant('h' + r.id);
    // CHAT-11/d: the input forms the message showed, in their current state.
    const forms = (Array.isArray(r.forms) ? r.forms : []).map(normalizeForm).filter(Boolean);
    out.push({
      ...m,
      forms,
      // A form that still needs the notice shows it; its text is read from GET /notice-acks.
      notice: forms.some((f) => f.noticeRequired) ? loadingNotice() : null,
      serverId: r.id,
      text: String(r.text || ''),
      citations: (r.citations || []).map((c) => ({ ...c, _key: JSON.stringify(c) })),
      charts: normalizeCharts(r.charts),
      ownedFree: r.owned === true,
      confirms: (r.confirmations || []).map((c) => normalizeConfirm(c, c && c.status)).filter(Boolean),
      // P4: the not_covered event as it was sent, stored with the message.
      notCovered: [normalizeNotCovered(r.notCovered)].filter(Boolean),
      // PFD-235: the prospects previews as they were sent, rebuilt through the same allow-list.
      prospects: normalizeProspectsList(r.prospects),
      error: r.error || null,
      // `streaming` = the turn is still running on the server: shown as answering
      // (spinner) and polled until it settles, never as a finished answer.
      status: r.status === 'error' || r.status === 'interrupted' ? 'error' : r.status === 'streaming' ? 'answering' : 'complete',
    });
  });
  // A closed offer followed by a newer card was re-offered by the server.
  out.forEach((m, i) => {
    (m.confirms || []).forEach((c) => {
      if (c.status === 'expired' || c.status === 'rejected') {
        c.reoffered = out.slice(i + 1).some((n) => n.confirms && n.confirms.length > 0);
      }
    });
  });
  return out;
}

// ---------------------------------------------------------------------------
// "What stands out?" (CHAT-20)
// ---------------------------------------------------------------------------

export const INSIGHT_KINDS = ['trend', 'peers', 'risk'];

const wholeNumber = (v, max) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v) : NaN;
  return Number.isInteger(n) && n >= 0 && n <= max ? n : null;
};

// How far the page's IF profile is from being THIS report's:
//   pending  no profile data yet (still loading)
//   missing  data is there but carries no reportID: we cannot tell which report it is
//   stale    the reportID is another report's (the store still holds the previous one)
//   ok       reportID present and equal to the page's report id
export function insightsIdentity({ profile, reportId } = {}) {
  if (!profile || typeof profile !== 'object' || profile.bznaics == null) return 'pending';
  if (profile.reportID == null || String(profile.reportID).trim() === '') return 'missing';
  if (reportId == null || String(profile.reportID) !== String(reportId)) return 'stale';
  return 'ok';
}

// POST /insights body from the report page's own data (the IF profile the page
// loaded: bznaics, area_id, sale_class_id) plus the page's report id. Null unless the
// profile's reportID is present and equal to the page's (insightsIdentity), or is still the PREVIOUS report's, or is not a plain
// combination (a sales class such as "2a" has no number to send).
export function buildInsightsRequest({ profile, reportId } = {}) {
  if (insightsIdentity({ profile, reportId }) !== 'ok') return null;
  const bznaics = String(profile.bznaics == null ? '' : profile.bznaics).trim().replace(/\.0+$/, '');
  if (!/^\d{2,8}(\.\d+)?$/.test(bznaics)) return null;
  const area = wholeNumber(profile.area_id, 99999999);
  const sale = wholeNumber(profile.sale_class_id, 99999);
  if (area == null || sale == null) return null;
  const id = wholeNumber(reportId, Number.MAX_SAFE_INTEGER);
  if (!id) return null;
  return { context: { product: 'if', reportId: id, combination: { bznaics, area_id: area, sale_class_id: sale } } };
}

// The service could not confirm the report id it was sent (409 report_unverified, or a legacy 200
// verified:false): no insights are shown and the report stays locked until the page reloads.
export const INSIGHTS_UNVERIFIED_VIEW = { kind: 'message', text: "Couldn't verify this Industry Financial Profile", retryable: false, unverified: true };
export const insightsUnverified = (body) => !!body && typeof body === 'object' && body.verified === false;

// The 200 body -> what the card shows (trend, peers, risk order, one per kind),
// or null when nothing usable came back. All text is untrusted display text.
export function normalizeInsights(body) {
  if (!body || typeof body !== 'object' || !Array.isArray(body.insights)) return null;
  if (insightsUnverified(body)) return null;
  const byKind = {};
  body.insights.forEach((i) => {
    if (!i || typeof i !== 'object' || INSIGHT_KINDS.indexOf(i.kind) === -1 || byKind[i.kind]) return;
    const title = cleanDisplay(i.title, 160);
    const detail = cleanDisplay(i.detail, 700);
    if (!title || !detail) return;
    byKind[i.kind] = { kind: i.kind, title, detail, section: cleanDisplay(i.section, 100), followUp: cleanDisplay(i.followUp, 300) };
  });
  const insights = INSIGHT_KINDS.map((k) => byKind[k]).filter(Boolean);
  if (!insights.length) return null;
  return { insights, complete: body.complete === true && insights.length === INSIGHT_KINDS.length };
}

// What "Ask about this" sends to the panel.
export function insightQuestion(i) {
  const f = cleanDisplay(i && i.followUp, 1000);
  if (f) return f;
  const t = cleanDisplay(i && i.title, 200);
  return t ? 'Tell me more about: ' + t : '';
}

// Same rule as every chat entry point (chat/visible), then the teaser shows it locked.
export function insightsEntry({ visible, mode } = {}) {
  if (!visible) return 'hidden';
  return mode === 'teaser' ? 'locked' : 'button';
}

export const INSIGHTS_LIMIT_TEXT = 'Daily insight limit reached.';

// A final per-report answer: the daily limit, or the report could not be verified.
// The button stays off and nothing is POSTed again until the page reloads.
export function insightsLocked(have) {
  return !!have && have.phase === 'error' && !!have.view && (!!have.view.limited || !!have.view.unverified);
}

// Whether a "What stands out?" click may POST. A running or finished request is
// reused; after a final answer (insightsLocked) nothing is asked again (not even Retry);
// any other error is asked again only with `force` (Retry).
export function shouldRequestInsights(have, force) {
  if (!have) return true;
  if (insightsLocked(have)) return false;
  return have.phase === 'error' && !!force;
}

const NO_INSIGHTS = "These insights aren't available for this Industry Financial Profile.";

// A failed POST /insights (a ChatApiError, by shape) -> what the page shows. The text is
// always ours, never the service's message.
//   not_owned  the report is not owned: no button, a short note
//   locked     403 subscription_required: the teaser lock
//   message    short and final (no Retry)
//   retry      Retry offered
export function insightsErrorView(e) {
  const code = e && e.code;
  if (code === 'not_owned') return { kind: 'not_owned', text: 'Available for Industry Financial Profiles you own', retryable: false };
  if (code === 'subscription_required') {
    return e.retryable === true
      ? { kind: 'retry', text: "Couldn't check your subscription.", retryable: true }
      : { kind: 'locked', text: '', retryable: false };
  }
  if (code === 'report_mismatch' || code === 'invalid_request' || code === 'combination_required') return { kind: 'message', text: NO_INSIGHTS, retryable: false };
  if (code === 'report_unverified') return { ...INSIGHTS_UNVERIFIED_VIEW };
  if (code === 'insights_limit') return { kind: 'message', text: INSIGHTS_LIMIT_TEXT, retryable: false, limited: true };
  if (code === 'daily_message_limit') return { kind: 'message', text: limitReachedView(parseResetAt(e.resetAt)).text, retryable: false, limited: true };
  if (code === 'too_many_turns') return { kind: 'retry', text: 'Ask Bizminer is busy with another answer. Try again in a moment.', retryable: true };
  return { kind: 'retry', text: "Couldn't read this Industry Financial Profile just now.", retryable: true };
}

// ---------------------------------------------------------------------------
// Settling (F1/F2): poll GET /conversations/:id while an answer or a purchase is
// still running on the server.
// ---------------------------------------------------------------------------

export const SETTLE_POLL_MS = 3000;
export const SETTLE_MAX_POLLS = 20; // 20 x 3 s = 60 s
// P3: a purchase still redeeming after that is polled more slowly, for 5 more minutes.
export const SLOW_POLL_MS = 15000;
export const SLOW_MAX_POLLS = 20; // 20 x 15 s = 5 min

export function needsSettle(messages) {
  return (messages || []).some(
    (m) => m.role === 'assistant' && (m.status === 'answering' || (m.confirms || []).some((c) => c.status === 'redeeming'))
  );
}

const hasAnswering = (messages) => (messages || []).some((m) => m.role === 'assistant' && m.status === 'answering');

// One step of the settle loop; `attempt` counts the polls made so far.
//  {action: 'stop'}               settled
//  {action: 'wait', delayMs}      poll again after delayMs
//  {action: 'giveUp'}             60 s passed with an answer still running: show it interrupted
//  {action: 'stalled'}            the slow polling is over too: show "taking longer than usual"
// An answer is given up after the fast phase only; a redeeming card then goes on
// with the slow phase.
export function settleStep({ messages, attempt }) {
  if (!needsSettle(messages)) return { action: 'stop' };
  if (attempt < SETTLE_MAX_POLLS) return { action: 'wait', delayMs: SETTLE_POLL_MS };
  if (hasAnswering(messages)) return { action: 'giveUp' };
  if (attempt < SETTLE_MAX_POLLS + SLOW_MAX_POLLS) return { action: 'wait', delayMs: SLOW_POLL_MS };
  return { action: 'stalled' };
}

// D3: whether the composer stays disabled while polling. The fast 60 s (and any
// answer still running on the server) lock it; the slow phase of a redeeming
// card does not, so the user can type and send while the card polls quietly.
export function settleLocksComposer({ messages, attempt }) {
  if (!needsSettle(messages)) return false;
  return hasAnswering(messages) || attempt < SETTLE_MAX_POLLS;
}

// ---------------------------------------------------------------------------
// The /chat page (CHAT-13): conversation history, search, delete, phone layout
// ---------------------------------------------------------------------------
// Backend: GET /conversations?limit&cursor&q -> {items:[{id,title,updatedAt,messageCount}],
// nextCursor, emptyState}; DELETE /conversations/:id -> 204 | 404 not_found | 409 conversation_busy.

export const CONVERSATIONS_PAGE_SIZE = 20;
export const CONVERSATIONS_MAX_LIMIT = 50; // the server accepts 1..50
export const SEARCH_DEBOUNCE_MS = 300; // CHAT-13/b
export const SEARCH_MAX_LEN = 100; // the server's cap on `q`

// What is searched for: trimmed and cut to what the server accepts.
export function normalizeSearch(raw) {
  return String(raw == null ? '' : raw).trim().slice(0, SEARCH_MAX_LEN);
}

// '?limit=20&cursor=...&q=...': empty parts are left out.
export function conversationsQuery({ limit, cursor, q } = {}) {
  const n = Number.isFinite(limit) ? Math.min(CONVERSATIONS_MAX_LIMIT, Math.max(1, Math.floor(limit))) : CONVERSATIONS_PAGE_SIZE;
  const parts = ['limit=' + n];
  if (typeof cursor === 'string' && cursor !== '') parts.push('cursor=' + encodeURIComponent(cursor));
  const term = normalizeSearch(q);
  if (term) parts.push('q=' + encodeURIComponent(term));
  return '?' + parts.join('&');
}

const UNTITLED = 'Untitled conversation';

// A GET /conversations answer as the page uses it. Rows without a usable id are
// dropped. A body that cannot be read is an empty page: it never claims the
// "no conversations at all" state (only the service's own emptyState does).
export function normalizeConversationList(body) {
  const rows = body && Array.isArray(body.items) ? body.items : [];
  const items = [];
  rows.forEach((r) => {
    if (!r || typeof r.id !== 'string' || r.id === '') return;
    const title = typeof r.title === 'string' ? r.title.trim() : '';
    const t = typeof r.updatedAt === 'string' ? new Date(r.updatedAt) : null;
    items.push({
      id: r.id,
      title: title || UNTITLED,
      updatedAt: t && !isNaN(t.getTime()) ? r.updatedAt : null,
      messageCount: Number.isFinite(r.messageCount) ? r.messageCount : 0,
      // CHAT-08: when the owner shared it with support; null when not shared.
      sharedAt: typeof r.sharedAt === 'string' && !isNaN(new Date(r.sharedAt).getTime()) ? r.sharedAt : null,
    });
  });
  return {
    items,
    nextCursor: body && typeof body.nextCursor === 'string' && body.nextCursor !== '' ? body.nextCursor : null,
    emptyState: !!body && body.emptyState === true,
  };
}

// The next page goes after what is shown; a row seen twice appears once (the one already shown wins).
export function mergeConversationPage(existing, items) {
  const seen = new Set((existing || []).map((i) => i.id));
  return (existing || []).concat((items || []).filter((i) => !seen.has(i.id)));
}

// Re-reading page 1 after a turn: new and moved conversations come first, the
// older pages already loaded stay. While some of them remain, the cursor is the
// one that followed them; when page 1 covers everything shown, it is page 1's.
export function mergeConversationHead(existing, existingCursor, page) {
  const head = (page && page.items) || [];
  const ids = new Set(head.map((i) => i.id));
  const tail = (existing || []).filter((i) => !ids.has(i.id));
  return {
    items: head.concat(tail),
    nextCursor: tail.length ? existingCursor || null : (page && page.nextCursor) || null,
  };
}

// What the left pane shows: loading | error | list | nomatch | empty.
// Rows already shown are never hidden by a refresh or a failed refresh.
export function conversationListView({ items, query, loading, error } = {}) {
  if (items && items.length) return 'list';
  if (loading) return 'loading';
  if (error) return 'error';
  return normalizeSearch(query) ? 'nomatch' : 'empty';
}

// A (re)load of page 1 clears the rows only when the query changed: rows of another query must not stay on
// screen or be paged; a same-query reload (reconnect, gate return, Retry) keeps them, and so does a failed one.
export function reloadClearsRows(query, pageQuery) {
  return normalizeSearch(query) !== normalizeSearch(pageQuery);
}

export const noMatchText = (query) => 'No conversations match \u201c' + normalizeSearch(query) + '\u201d.';
export const EMPTY_LIST_TITLE = 'No conversations yet';
export const EMPTY_LIST_TEXT = 'Ask a question in the box on the right, or press New conversation. Ask Bizminer answers from Bizminer data and starts with the profiles and reports you already own. Your conversations will be listed here.';

export const DELETE_CONFIRM_TEXT = 'Delete this conversation for good? Its messages and any profile or report data in it are removed. What you bought stays in your account.';
export const DELETE_BUSY_TEXT = "Can't delete while an answer or purchase is in progress";
export const DELETE_FAILED_TEXT = "Couldn't delete this conversation. Please try again.";

// A failed DELETE /conversations/:id (a ChatApiError): what to tell the user and
// whether the row leaves the list.
//   busy         409 conversation_busy: an answer is streaming or a purchase is in flight; nothing was deleted
//   gone         404: already deleted (another tab): the row leaves the list
//   subscription the subscription lapsed: the page turns into the teaser
//   denied       not_in_beta / not_allowed: the page is hidden
//   failed       anything else (network, 5xx)
export function deleteFailure(e) {
  const status = e && e.status;
  const code = e && e.code;
  if (status === 409 && code === 'conversation_busy') return { kind: 'busy', text: DELETE_BUSY_TEXT, remove: false };
  if (status === 404) return { kind: 'gone', text: '', remove: true };
  if (isSubscriptionRequired(e)) return { kind: 'subscription', text: '', remove: false };
  if (code === 'not_in_beta' || code === 'not_allowed') return { kind: 'denied', text: '', remove: false };
  return { kind: 'failed', text: DELETE_FAILED_TEXT, remove: false };
}

// After a delete: the list without it, and what is open. Deleting the open
// conversation opens its neighbour (the one that took its place, else the one
// above). When its row is not in the list (a search hides it) there is no
// neighbour: a new conversation (null) opens. The deleted id is never kept.
export function afterDelete(items, id, selectedId) {
  const list = items || [];
  const at = list.findIndex((i) => i.id === id);
  const next = list.filter((i) => i.id !== id);
  if (selectedId !== id) return { items: next, selectedId: selectedId == null ? null : selectedId };
  const pick = at < 0 ? null : next[at] || next[at - 1] || null;
  return { items: next, selectedId: pick ? pick.id : null };
}

// The open conversation can change only while nothing is running in it: a
// stream, the server still settling an answer, or a purchase click.
export function canSwitchConversation({ busy, settleLocked, working } = {}) {
  return !(busy || settleLocked || working);
}

// The next page of the list: only for the query its rows were loaded with.
export function canLoadMore({ nextCursor, listLoading, moreLoading, moreError, retry, pageQuery, applied } = {}) {
  if (!nextCursor || listLoading || moreLoading) return false;
  if (moreError && retry !== true) return false;
  return (pageQuery || '') === (applied || '');
}

// Wide screens open the newest conversation on arrival, unless the user got
// there first: typed a draft, pressed New conversation, or has messages on screen.
export function shouldAutoSelect({ isPhone, selectedId, applied, count, typed, started, hasMessages } = {}) {
  if (isPhone || selectedId || applied || !count) return false;
  return !(typed || started || hasMessages);
}

// fn(...lastArgs) after `ms` of quiet. flush() runs a pending call now; cancel() drops it.
export function createDebouncer(fn, ms, timers) {
  const t = timers || { setTimeout: (f, n) => setTimeout(f, n), clearTimeout: (h) => clearTimeout(h) };
  let handle = null;
  let args = null;
  const run = () => {
    handle = null;
    const a = args;
    args = null;
    fn(...a);
  };
  return {
    call(...a) {
      if (handle !== null) t.clearTimeout(handle);
      args = a;
      handle = t.setTimeout(run, ms);
    },
    flush() {
      if (handle === null) return;
      t.clearTimeout(handle);
      run();
    },
    cancel() {
      if (handle !== null) t.clearTimeout(handle);
      handle = null;
      args = null;
    },
  };
}

// Infinite scroll: ask for the next page when the bottom is near, or when the
// rows do not fill their box (no scrollbar to scroll).
export function nearBottom(box, threshold = 120) {
  if (!box) return false;
  const { scrollTop, clientHeight, scrollHeight } = box;
  if (![scrollTop, clientHeight, scrollHeight].every((n) => Number.isFinite(n))) return false;
  return scrollHeight - scrollTop - clientHeight <= threshold;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayKey = (d) => d.getFullYear() * 10000 + d.getMonth() * 100 + d.getDate();

// "Today, 11:42 AM" | "Yesterday" | "Oct 18" | "Dec 3, 2025" (local time).
export function whenLabel(iso, now = new Date()) {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return '';
  if (dayKey(d) === dayKey(now)) {
    const h = d.getHours();
    const mm = String(d.getMinutes()).padStart(2, '0');
    return 'Today, ' + (h % 12 || 12) + ':' + mm + ' ' + (h < 12 ? 'AM' : 'PM');
  }
  const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (dayKey(d) === dayKey(y)) return 'Yesterday';
  const base = MONTHS[d.getMonth()] + ' ' + d.getDate();
  return d.getFullYear() === now.getFullYear() ? base : base + ', ' + d.getFullYear();
}

// Which panes show. Wide: both. Phone: one at a time (the list is where a visit
// starts), and the conversation gets a Back button.
export function chatPageLayout({ isPhone, pane } = {}) {
  if (!isPhone) return { list: true, convo: true, back: false };
  const convo = pane === 'convo';
  return { list: !convo, convo, back: convo };
}

// The page follows the panel's gate (computeChatVisible, then chatMode):
// hidden | teaser (locked) | full.
export function chatPageGate({ visible, mode } = {}) {
  if (!visible) return 'hidden';
  return mode === 'teaser' ? 'teaser' : 'full';
}

// ---------------------------------------------------------------------------
// Reports built from the customer's own figures (PFD-223 CHAT-11, PFD-236 CHAT-22)
// ---------------------------------------------------------------------------
// The financial-data notice, the input form, the client summary and the error
// codes of those turns. Contract: chat.bizminer.com README, "Financial Report
// and Valuation Report". Everything the service sends is display data: it is
// cleaned and bounded here and only ever rendered as text.
//   notice       {version, kind, title, text, actions: [{id: continue|standard_form, label}]}
//   form         {id, kind, product, combination, report, years, fields, values, status, reportId, noticeRequired}
//   form_result  {formId, status: saved|rejected|error, reportId, report, message, errors: {path: message}}
//   (the turn that shows a form ends with finishReason awaiting_input; the form is submitted with
//    POST /conversations/:id/forms/:formId/submit)

export const SUPPORT_EMAIL = 'support@bizminer.com';
const FORM_CHECK_MESSAGE = 'Check the fields marked below.';
const NOTICE_ACK_FAILED = "We couldn't save that. Please try again.";
const NOTICE_LOAD_FAILED = "Couldn't load the notice. Please try again.";

// ---- notice ----

const NOTICE_ACTIONS = [
  { id: 'continue', label: 'Continue' },
  { id: 'standard_form', label: 'Use the standard form instead' },
];

export function normalizeNotice(d, status = 'open') {
  if (!d || typeof d !== 'object' || typeof d.version !== 'string' || !d.version.trim()) return null;
  const given = Array.isArray(d.actions) ? d.actions : [];
  return {
    version: d.version.trim(),
    kind: 'financial_data',
    title: cleanDisplay(d.title, 120) || 'Before you enter your figures',
    text: cleanDisplay(d.text, 1200),
    actions: NOTICE_ACTIONS.map((a) => {
      const g = given.find((x) => x && x.id === a.id);
      return { id: a.id, label: (g && cleanDisplay(g.label, 60)) || a.label };
    }),
    status,
    error: null,
  };
}

// A notice whose text has not been read yet (a form reopened from history).
export function loadingNotice() {
  return { version: null, kind: 'financial_data', title: '', text: '', actions: NOTICE_ACTIONS.map((a) => ({ ...a })), status: 'loading', error: null };
}

// CHAT-11/e: the app's own report creation flow (routes.js 'create-report-format/:report'; CreateReportFormat.vue
// reads financial | market | valuation from that segment).
const STANDARD_SEGMENT = { valuation_report: 'valuation', market_report: 'market' };
export function standardFormPath(kind) {
  return '/create-report-format/' + (STANDARD_SEGMENT[kind] || 'financial');
}
const KIND_OF_PRODUCT = { 'Financial Report': 'financial_report', 'Valuation Report': 'valuation_report', 'Market Report': 'market_report' };
// The report kind a notice is about: its form's, else its Confirm card's product (a notice shown on an old card).
export function noticeStandardPath(m) {
  const f = m && Array.isArray(m.forms) ? m.forms[0] : null;
  if (f && f.kind) return standardFormPath(f.kind);
  const c = m && Array.isArray(m.confirms) ? m.confirms.find((x) => x && KIND_OF_PRODUCT[x.product]) : null;
  return standardFormPath(c ? KIND_OF_PRODUCT[c.product] : null);
}

function mapNotices(state, fn) {
  if (!state.messages.some((m) => m.notice)) return state;
  return { ...state, messages: state.messages.map((m) => (m.notice ? { ...m, notice: fn(m.notice) } : m)) };
}

// The notice was acknowledged: every form waiting for it opens.
function unlockForms(state) {
  if (!state.messages.some((m) => (m.forms || []).some((f) => f.noticeRequired))) return state;
  return {
    ...state,
    messages: state.messages.map((m) =>
      (m.forms || []).some((f) => f.noticeRequired) ? { ...m, forms: m.forms.map((f) => (f.noticeRequired ? { ...f, noticeRequired: false } : f)) } : m
    ),
  };
}

// A Confirm click was refused with 403 notice_required (a card offered before the notice version changed): show the
// notice on that card's answer. The card stays pending and the forms are not touched.
function noticeRequiredForCard(state, pendingId) {
  const id = String(pendingId);
  const has = (m) => (m.confirms || []).some((c) => c.pendingId === id);
  if (!state.messages.some(has)) return state;
  return {
    ...state,
    messages: state.messages.map((m) => (has(m) ? { ...m, notice: m.notice && m.notice.version ? { ...m.notice, status: 'open', error: null } : loadingNotice() } : m)),
  };
}

// A submission was refused with 403 notice_required: show the notice again on that form's answer.
function noticeRequiredFor(state, formId) {
  const has = (m) => (m.forms || []).some((f) => formId == null || f.id === formId);
  if (!state.messages.some(has)) return state;
  return {
    ...state,
    messages: state.messages.map((m) =>
      has(m)
        ? {
            ...m,
            forms: m.forms.map((f) => ({ ...f, noticeRequired: true, phase: 'editing' })),
            notice: m.notice && m.notice.version ? { ...m.notice, status: 'open', error: null } : loadingNotice(),
          }
        : m
    ),
  };
}

// GET /notice-acks answered: fill the waiting notices in, or open the forms when it was acknowledged elsewhere.
function noticeLoaded(state, body) {
  if (!body || typeof body !== 'object') return state;
  if (body.acknowledged === true) return unlockForms(mapNotices(state, (n) => ({ ...n, status: 'acked', error: null })));
  const fresh = normalizeNotice(body);
  if (!fresh) return state;
  return mapNotices(state, (n) => (n.status === 'loading' || n.status === 'failed' ? { ...fresh } : n));
}

// ---- forms ----

// A period label is a year ("2025") or, for a Market Report's current period, a month and year ("Jan-2026").
const PERIOD_RE = /^(\d{4}|[A-Z][a-z]{2}-\d{4})$/;
const FORM_PRODUCTS = { financial_report: 'Financial Report', valuation_report: 'Valuation Report', market_report: 'Market Report' };
const FIELD_TYPES = ['number', 'text', 'scale', 'boolean'];
const REQUIRED = ['every_year', 'latest_year', 'optional'];
const finiteOrNull = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
// A time as ms: a number, or an ISO string. null when it is neither.
const timeOf = (v) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Date.parse(v) : NaN;
  return isFinite(n) ? n : null;
};

function normalizeField(f) {
  if (!f || typeof f !== 'object' || typeof f.key !== 'string' || !f.key) return null;
  if (f.scope !== 'year' && f.scope !== 'company') return null;
  const required = REQUIRED.includes(f.required) ? f.required : 'optional';
  const unit = f.unit === 'usd' || f.unit === 'count' ? f.unit : null;
  return {
    key: f.key,
    // The key inside `inputs.{year}` or `company`: a company field's key carries its scope.
    prop: f.scope === 'company' ? f.key.replace(/^company\./, '') : f.key,
    title: cleanDisplay(f.title, 120) || f.key,
    section: cleanDisplay(f.section, 64) || (f.scope === 'company' ? 'company' : 'financials'),
    sectionTitle: cleanDisplay(f.sectionTitle, 120) || (f.scope === 'company' ? 'Company' : 'Financials'),
    scope: f.scope,
    type: FIELD_TYPES.includes(f.type) ? f.type : 'number',
    unit,
    prefix: unit === 'usd' ? '$' : '',
    required,
    isRequired: required !== 'optional',
    mayBeNegative: f.mayBeNegative === true,
    zeroAllowed: f.zeroAllowed !== false,
    min: finiteOrNull(f.min),
    max: finiteOrNull(f.max),
    step: finiteOrNull(f.step),
    chatOnly: f.chatOnly === true,
    description: cleanDisplay(f.description, 400),
  };
}

function normalizeFormReport(r) {
  if (!r || typeof r !== 'object') return null;
  const part = (x) => (x && typeof x === 'object' ? { id: x.id != null ? x.id : null, code: x.code != null ? String(x.code) : null, name: x.name != null ? cleanDisplay(x.name, 120) : null } : null);
  return { id: r.id != null ? r.id : null, industry: part(r.industry), area: part(r.area), salesClass: part(r.salesClass) };
}

// [plain years ascending, then "Mon-YYYY" labels by year then month]. A month name we do not know sorts first in its year.
const periodKey = (label) => {
  const m = /^([A-Za-z]{3})-(\d{4})$/.exec(label);
  if (!m) return [0, Number(label), 0];
  return [1, Number(m[2]), MONTHS.indexOf(m[1]) + 1];
};
export function sortPeriods(labels) {
  return labels
    .map((l, i) => ({ l, i, k: periodKey(l) }))
    .sort((a, b) => a.k[0] - b.k[0] || a.k[1] - b.k[1] || a.k[2] - b.k[2] || a.i - b.i)
    .map((x) => x.l);
}

export function normalizeForm(d) {
  if (!d || typeof d !== 'object' || d.id == null || d.id === '') return null;
  // Chronological, whatever order they arrive in: the plain years oldest first, then the month periods
  // ("Jan-2026") by year and month. The last label is always the latest, the current period.
  const years = sortPeriods([...new Set((Array.isArray(d.years) ? d.years : []).map((y) => String(y).trim()).filter((y) => PERIOD_RE.test(y)))]);
  const fields = (Array.isArray(d.fields) ? d.fields : []).map(normalizeField).filter(Boolean);
  if (!years.length || !fields.length) return null;
  const v = d.values && typeof d.values === 'object' ? d.values : {};
  const reportId = typeof d.reportId === 'number' && isFinite(d.reportId) ? d.reportId : null;
  const c = d.combination && typeof d.combination === 'object' ? d.combination : {};
  const kind = FORM_PRODUCTS[d.kind] ? d.kind : 'financial_report';
  return {
    id: String(d.id),
    kind,
    product: cleanDisplay(d.product, 60) || FORM_PRODUCTS[kind],
    combination: { bznaics: c.bznaics != null ? String(c.bznaics) : '', area_id: c.area_id != null ? c.area_id : null, sale_class_id: c.sale_class_id != null ? c.sale_class_id : null },
    report: normalizeFormReport(d.report),
    years,
    fields,
    values: {
      inputs: v.inputs && typeof v.inputs === 'object' && !Array.isArray(v.inputs) ? v.inputs : {},
      company: v.company && typeof v.company === 'object' && !Array.isArray(v.company) ? v.company : {},
    },
    status: d.status === 'submitted' ? 'submitted' : 'open',
    reportId,
    noticeRequired: d.noticeRequired === true,
    // When the service last changed the form (ms), to tell its values from an older typed draft; null when unknown.
    updatedAt: timeOf(d.updatedAt),
    // What this panel is doing with the form: editing | submitting. `result` is how the last
    // submission went (saved | rejected | error) and `errors` / `message` what the service said.
    phase: 'editing',
    result: null,
    errors: {},
    message: '',
  };
}

// Sections in the order they arrive: the yearly ones (one column per year), then the company block.
export function formSections(form) {
  const out = [];
  (form ? form.fields : []).forEach((f) => {
    let s = out.find((x) => x.id === f.section && x.scope === f.scope);
    if (!s) {
      s = { id: f.section, title: f.sectionTitle, scope: f.scope, fields: [] };
      out.push(s);
    }
    s.fields.push(f);
  });
  return out.filter((s) => s.scope === 'year').concat(out.filter((s) => s.scope === 'company'));
}

const AREA_US = 9999;
function slicePart(x) {
  if (!x) return '';
  return x.name ? (x.code ? x.name + ' (' + x.code + ')' : x.name) : x.code ? String(x.code) : '';
}
// "Full-Service Restaurants (722511) · United States · Industry-wide"; ids until the service named them.
export function formSubtitle(form) {
  if (!form) return '';
  const r = form.report;
  if (r && (r.industry || r.area || r.salesClass)) {
    return [slicePart(r.industry), r.area && r.area.name, r.salesClass && r.salesClass.name].filter(Boolean).join(' · ');
  }
  const c = form.combination || {};
  return [
    c.bznaics ? 'Industry ' + c.bznaics : '',
    c.area_id === AREA_US ? 'United States' : c.area_id != null ? 'Area ' + c.area_id : '',
    c.sale_class_id === 1 ? 'Industry-wide' : c.sale_class_id != null ? 'Sales class ' + c.sale_class_id : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

export function valuationLockNote(form) {
  return form && form.kind === 'valuation_report' && form.reportId != null ? "The company name can't change after the report first runs." : '';
}

// ---- drafts: what is typed in the fields ----
// {inputs: {year: {prop: 'text as typed'}}, company: {prop: 'text' | true | false | null}}

const MAX_TYPED = 40;
const MAX_TEXT = 200;

// "1400000" -> "1,400,000". Anything that is not a plain number is left as typed.
export function formatDraftNumber(text) {
  const raw = text == null ? '' : String(text);
  const t = raw.replace(/[,\s]/g, '');
  const m = /^(-?)(\d+)(\.\d*)?$/.exec(t);
  if (!m) return raw;
  const int = m[2].replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return m[1] + int + (m[3] || '');
}

// A typed number: null (blank), a number, or NaN (not a number).
function typedNumber(v) {
  if (v == null) return null;
  const t = String(v).replace(/[,\s]/g, '');
  if (t === '') return null;
  return /^-?(\d+\.?\d*|\.\d+)$/.test(t) ? Number(t) : NaN;
}
const isBlank = (v) => v == null || (typeof v === 'string' && v.trim() === '');

function shown(v, field) {
  if (v == null) return field.type === 'boolean' ? null : '';
  if (field.type === 'boolean') return v === true ? true : v === false ? false : null;
  if (field.type === 'number') return formatDraftNumber(String(v));
  return String(v).slice(0, MAX_TEXT);
}

export function draftFromForm(form) {
  const draft = { inputs: {}, company: {} };
  if (!form) return draft;
  const vi = (form.values && form.values.inputs) || {};
  const vc = (form.values && form.values.company) || {};
  form.years.forEach((y) => {
    const row = vi[y];
    if (!row || typeof row !== 'object') return;
    form.fields.filter((f) => f.scope === 'year').forEach((f) => {
      if (row[f.prop] != null) draft.inputs[y] = { ...(draft.inputs[y] || {}), [f.prop]: shown(row[f.prop], f) };
    });
  });
  form.fields.filter((f) => f.scope === 'company').forEach((f) => {
    if (vc[f.prop] != null) draft.company[f.prop] = shown(vc[f.prop], f);
  });
  return draft;
}

export function setDraftValue(draft, field, year, value) {
  const d = draft || { inputs: {}, company: {} };
  if (field.scope === 'company') return { ...d, company: { ...d.company, [field.prop]: value } };
  return { ...d, inputs: { ...d.inputs, [year]: { ...((d.inputs && d.inputs[year]) || {}), [field.prop]: value } } };
}

// Drafts live in sessionStorage (a reload keeps them, closing the tab or signing out does not). A draft is the
// customer's own figures, so it is kept per user: with no user there is no key and nothing is stored.
export const DRAFT_PREFIX = 'bzchat.formdraft.';
export const draftStorageKey = (uid, formId) => (uid == null || uid === '' ? null : DRAFT_PREFIX + uid + '.' + formId);

// What goes into storage: the draft, the time it was saved (ms, the browser's clock: kept for old drafts) and
// `baseUpdatedAt`, the server's form.updatedAt (ms, or null) as seen when the draft was written. The browser's
// clock is never compared with the server's: only the server's own times are compared with each other.
export const serializeDraft = (draft, now = Date.now(), baseUpdatedAt = null) =>
  JSON.stringify({ inputs: (draft && draft.inputs) || {}, company: (draft && draft.company) || {}, savedAt: now, baseUpdatedAt: baseUpdatedAt == null ? null : baseUpdatedAt });

// Forget one stored draft (the form was saved: the service holds those figures now).
export function forgetDraft(storage, uid, formId) {
  const key = draftStorageKey(uid, formId);
  if (!key) return;
  try {
    storage.removeItem(key);
  } catch (e) {
    /* storage unavailable: nothing was kept */
  }
}

// A stored draft, cleaned against the form: its years and fields only, text only, bounded. null = nothing usable.
export function parseStoredDraft(raw, form) {
  if (!raw || !form) return null;
  let o;
  try {
    o = JSON.parse(raw);
  } catch (e) {
    return null;
  }
  if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
  const draft = { inputs: {}, company: {}, savedAt: timeOf(o.savedAt) };
  // undefined = a draft written before the base was kept (legacy); null = it was started with no server time.
  if ('baseUpdatedAt' in o) draft.baseUpdatedAt = timeOf(o.baseUpdatedAt);
  const inputs = o.inputs && typeof o.inputs === 'object' ? o.inputs : {};
  const company = o.company && typeof o.company === 'object' ? o.company : {};
  form.years.forEach((y) => {
    const row = inputs[y];
    if (!row || typeof row !== 'object') return;
    form.fields.filter((f) => f.scope === 'year').forEach((f) => {
      const v = row[f.prop];
      if (typeof v === 'string' || typeof v === 'number') draft.inputs[y] = { ...(draft.inputs[y] || {}), [f.prop]: String(v).slice(0, MAX_TYPED) };
    });
  });
  form.fields.filter((f) => f.scope === 'company').forEach((f) => {
    const v = company[f.prop];
    if (f.type === 'boolean') {
      if (v === true || v === false) draft.company[f.prop] = v;
    } else if (typeof v === 'string' || typeof v === 'number') {
      draft.company[f.prop] = String(v).slice(0, f.type === 'text' ? MAX_TEXT : MAX_TYPED);
    }
  });
  return draft;
}

// Forget every stored draft (sign-out, another user): the figures are the customer's own.
export function clearStoredDrafts(storage) {
  try {
    for (let i = storage.length - 1; i >= 0; i--) {
      const k = storage.key(i);
      if (k && k.indexOf(DRAFT_PREFIX) === 0) storage.removeItem(k);
    }
  } catch (e) {
    /* storage unavailable: nothing was kept */
  }
}

// A notice or form waiting for its text from GET /notice-acks.
export const needsNoticeText = (messages) => (messages || []).some((m) => m.notice && m.notice.status === 'loading');

// Typed text wins over the service's saved values, unless someone saved the form after the draft was started
// (another tab or device): the form's updatedAt is then not the one the draft was built on, and the service's values
// show. Both times are the server's. A legacy draft (no baseUpdatedAt) is compared by its savedAt, as before.
// With nothing typed, the saved values show.
export function chooseDraft(form, stored) {
  if (!stored) return draftFromForm(form);
  if (!form || form.updatedAt == null) return stored;
  const changed = stored.baseUpdatedAt !== undefined ? form.updatedAt !== stored.baseUpdatedAt : form.updatedAt > (stored.savedAt == null ? 0 : stored.savedAt);
  return changed ? draftFromForm(form) : stored;
}

const draftRow = (draft, y) => (draft && draft.inputs && draft.inputs[y]) || {};
const yearHasFigure = (form, draft, y) => form.fields.some((f) => f.scope === 'year' && !isBlank(draftRow(draft, y)[f.prop]));

// What goes to POST .../submit: numbers (commas gone), never blanks. Something that is not a number is sent as
// typed so the service names the field. The company block only when something was entered.
export function buildSubmitValues(form, draft) {
  const inputs = {};
  form.years.forEach((y) => {
    const row = draftRow(draft, y);
    const out = {};
    form.fields.filter((f) => f.scope === 'year').forEach((f) => {
      const raw = row[f.prop];
      if (isBlank(raw)) return;
      const n = typedNumber(raw);
      out[f.prop] = Number.isNaN(n) ? String(raw).trim() : n;
    });
    if (Object.keys(out).length) inputs[y] = out;
  });
  const company = {};
  const dc = (draft && draft.company) || {};
  form.fields.filter((f) => f.scope === 'company').forEach((f) => {
    const raw = dc[f.prop];
    if (f.type === 'boolean') {
      if (raw === true || raw === false) company[f.prop] = raw;
    } else if (!isBlank(raw)) {
      if (f.type === 'text') company[f.prop] = String(raw).replace(/\s+/g, ' ').trim();
      else {
        const n = typedNumber(raw);
        company[f.prop] = Number.isNaN(n) ? String(raw).trim() : n;
      }
    }
  });
  return Object.keys(company).length ? { inputs, company } : { inputs };
}

// The obvious problems, found before anything is sent. The service checks the rest (it is the authority) and
// names fields by the same paths: inputs.{year}.{key}, inputs, company.{key}.
export function checkDraft(form, draft) {
  const errors = {};
  const yearFields = form.fields.filter((f) => f.scope === 'year');
  const latest = form.years[form.years.length - 1];
  const withFigure = form.years.filter((y) => yearHasFigure(form, draft, y));
  if (!withFigure.length) {
    errors.inputs = 'Enter the figures for at least one year.';
  } else {
    form.years.forEach((y) => {
      const row = draftRow(draft, y);
      yearFields.forEach((f) => {
        const path = 'inputs.' + y + '.' + f.prop;
        if (isBlank(row[f.prop])) {
          const need = (f.required === 'every_year' && withFigure.includes(y)) || (f.required === 'latest_year' && y === latest);
          if (need) errors[path] = f.title + ' is required for ' + y + '.';
          return;
        }
        const n = typedNumber(row[f.prop]);
        if (Number.isNaN(n)) errors[path] = f.title + ' must be a number.';
        else if (n < 0 && !f.mayBeNegative) errors[path] = f.title + " can't be negative.";
        else if (n === 0 && !f.zeroAllowed) errors[path] = f.title + ' must be more than 0.';
      });
    });
  }
  const dc = (draft && draft.company) || {};
  form.fields.filter((f) => f.scope === 'company').forEach((f) => {
    const raw = dc[f.prop];
    const path = 'company.' + f.prop;
    if (f.type === 'text') {
      const t = isBlank(raw) ? '' : String(raw).replace(/\s+/g, ' ').trim();
      if (t.length > (f.max || 100)) errors[path] = f.title + ' must be at most ' + (f.max || 100) + ' characters.';
      else if (/[<>]/.test(t)) errors[path] = f.title + ' must be plain text.';
    } else if (f.type === 'scale' && !isBlank(raw)) {
      const n = typedNumber(raw);
      const min = f.min != null ? f.min : 0;
      const max = f.max != null ? f.max : 10;
      const step = f.step || 0.25;
      if (Number.isNaN(n) || n < min || n > max || Math.abs(n / step - Math.round(n / step)) > 1e-9) {
        errors[path] = f.title + ' must be between ' + min + ' and ' + max + ' in steps of ' + step + '.';
      }
    }
  });
  return errors;
}

// ---- errors by field ----

// The service's `{path: message}` (400 invalid_inputs `fields`, form_result `errors`), cleaned.
export function normalizeFieldErrors(fields) {
  const out = {};
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return out;
  Object.keys(fields).forEach((k) => {
    const msg = typeof fields[k] === 'string' ? cleanDisplay(fields[k], 300) : '';
    if (k && msg) out[k] = msg;
  });
  return out;
}

export function fieldErrorFor(errors, field, year) {
  if (!errors || !field) return '';
  const path = field.scope === 'company' ? 'company.' + field.prop : 'inputs.' + year + '.' + field.prop;
  return errors[path] || '';
}

// Errors that name no field this form shows (the form as a whole, a year, an unknown field), as a list.
export function unmappedFormErrors(form, errors) {
  const known = new Set();
  form.fields.forEach((f) => {
    if (f.scope === 'company') known.add('company.' + f.prop);
    else form.years.forEach((y) => known.add('inputs.' + y + '.' + f.prop));
  });
  return [...new Set(Object.keys(errors || {}).filter((k) => !known.has(k)).map((k) => errors[k]))];
}

function patchForm(state, formId, fn) {
  const id = formId == null ? null : String(formId);
  if (id == null || !state.messages.some((m) => (m.forms || []).some((f) => f.id === id))) return state;
  return {
    ...state,
    messages: state.messages.map((m) => ((m.forms || []).some((f) => f.id === id) ? { ...m, forms: m.forms.map((f) => (f.id === id ? fn(f) : f)) } : m)),
  };
}

// form_result: how a submission went. `saved`: the report exists (the Confirm card follows). `rejected`: the service
// (or mcp) refused the figures, with the field it names. `error`: they could not be saved right now.
function applyFormResult(state, data) {
  if (!data || typeof data !== 'object' || data.formId == null) return state;
  if (data.status !== 'saved' && data.status !== 'rejected' && data.status !== 'error') return state;
  return patchForm(state, data.formId, (f) => {
    const reportId = typeof data.reportId === 'number' ? data.reportId : f.reportId;
    const report = normalizeFormReport(data.report) || f.report;
    if (data.status === 'saved') return { ...f, result: 'saved', status: 'submitted', reportId, report, errors: {}, message: '' };
    return { ...f, result: data.status, reportId, report, errors: normalizeFieldErrors(data.errors), message: cleanDisplay(data.message, 500) };
  });
}

// ---- answers and the client summary ----

// "Open Financial Report" / "Open Valuation Report" / "Open Market Report" on a citation of those reports.
export function citationOpenLabel(c) {
  return c && (c.product === 'Financial Report' || c.product === 'Valuation Report' || c.product === 'Market Report') ? 'Open ' + c.product : 'Open';
}

export const SUMMARY_PRODUCTS = ['Financial Report', 'Valuation Report', 'Market Report'];
export const SUMMARY_FALLBACK_NAME = 'Client business';

// The "Create a one-page client summary" action of an answer: null (not offered), 'wait' (offered, but something
// is running) or 'available'. Only a finished answer that cites a report the user owns, with a stored message id.
export function summaryAction({ message, conversationId, busy } = {}) {
  if (!message || message.role !== 'assistant' || message.status !== 'complete' || message.serverId == null || !conversationId) return null;
  const hit = (message.citations || []).some((c) => c && SUMMARY_PRODUCTS.includes(c.product) && c.owned === true);
  if (!hit) return null;
  return busy ? 'wait' : 'available';
}

export function summaryErrorText(e) {
  if (e && e.code === 'no_report') return 'This answer has no report to summarize.';
  if (e && e.status === 404) return 'This answer is no longer available. Reopen the conversation and try again.';
  return "Couldn't create the summary. Please try again.";
}

const isNum = (v) => typeof v === 'number' && isFinite(v);
// 72 -> "72", 5.2 -> "5.2", 2.756 -> "2.76", 1400000 -> "1,400,000"
const plain = (n) => Number(n.toFixed(2)).toLocaleString('en-US', { maximumFractionDigits: 2 });
const moneyFull = (n) => (n < 0 ? '-$' + plain(-n) : '$' + plain(n));
const moneyShort = (n) => (Math.abs(n) >= 1e6 ? (n < 0 ? '-$' : '$') + (Math.abs(n) / 1e6).toFixed(2) + 'M' : moneyFull(n));
// 11 -> "11.0%", 5.25 -> "5.3%", 1234.56 -> "1,234.6%"
const percentOf = (n) => Number(n.toFixed(1)).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';
const PERCENT_UNIT = /^(%|pct|percent|percentage|percent[ _-]of[ _-]sales|%[ _-]of[ _-]sales)$/;
const NOT_PERCENT_WORDS = /\b(days?|turnover|coverage|times|ratio|multiple)\b/;
const PERCENT_WORDS = /%|\b(pct|percent|margin|roa|roe)\b|\breturn on\b/;

// How a variance row's "business" and "industry" values are shown: 'percent' (one decimal), 'usd', 'days', 'ratio'
// (two decimals), 'count' (an integer) or 'none' (a plain number). The chat service's `unit` (pct_of_sales | percent |
// usd | ratio | days | count) decides it when it sends one; the older spellings of a percent still work. Without a
// unit (an older service) it is guessed: from the metric path (income statement lines are common-size, percent of
// sales), then the key and title words (days, turnover, coverage and "ratio" lines are plain numbers; margins and
// expense lines are percents).
const UNIT_KIND = { pct_of_sales: 'percent', percent: 'percent', usd: 'usd', days: 'days', ratio: 'ratio', count: 'count' };
export function varianceUnit(x) {
  if (!x || typeof x !== 'object') return 'none';
  if (typeof x.unit === 'string' && x.unit.trim()) {
    const u = x.unit.trim().toLowerCase();
    if (UNIT_KIND[u]) return UNIT_KIND[u];
    if (PERCENT_UNIT.test(u)) return 'percent';
  }
  const path = x.sources && x.sources.yourFirm && x.sources.yourFirm.metric;
  if (typeof path === 'string' && /(^|\.)income_statement(\.|$)/.test(path)) return 'percent';
  const words = (String(x.key || '') + ' ' + String(x.title || '')).toLowerCase().replace(/_/g, ' ');
  if (NOT_PERCENT_WORDS.test(words)) return 'none';
  if (PERCENT_WORDS.test(words) || /expense/i.test(String(x.category || ''))) return 'percent';
  return 'none';
}
const ratioOf = (n) => Number(n.toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const countOf = (n) => Math.round(n).toLocaleString('en-US');
const daysOf = (n) => plain(n) + (Number(n.toFixed(2)) === 1 ? ' day' : ' days');
const VARIANCE_FORMAT = { percent: percentOf, usd: moneyFull, days: daysOf, ratio: ratioOf, count: countOf, none: plain };
const signed = (n) => (n < 0 ? '-' : '+') + plain(Math.abs(n)) + '%';
// The period a label names: "Business revenue 2025" -> "2025", "Annual sales Jan-2026" -> "Jan-2026"; '' when none.
const yearIn = (label) => {
  const m = /\b((?:[A-Z][a-z]{2}-)?(?:19|20)\d{2})\b/.exec(String(label || ''));
  return m ? m[1] : '';
};
// "2025" -> "FY2025" (a fiscal year); a Market Report's periods and a month period ("Jan-2026") are shown as they are.
const periodLabel = (p, market) => (!market && /^\d{4}$/.test(p) ? 'FY' + p : p);

export function ratingTone(rating) {
  const r = String(rating || '').toLowerCase();
  if (/healthy|good|strong/.test(r)) return 'good';
  if (/flag|poor|weak|critical/.test(r)) return 'bad';
  if (/slightly|watch|caution/.test(r)) return 'warn';
  return 'neutral';
}

const longDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
};
const SUMMARY_APPROACH = { earnings_approach: 'Earnings approach', sales_approach: 'Sales approach' };
const DISCLAIMERS = {
  financial_report: 'Benchmarks, not financial advice.',
  valuation_report: 'Estimates from industry transaction data, not an appraisal and not financial advice.',
  market_report: 'Benchmarks against the local industry market, not financial advice. Built from the Market Report and the figures entered for it.',
};
const SUMMARY_TITLES = { financial_report: 'Client Benchmark Summary', valuation_report: 'Business Valuation Summary', market_report: 'Market Summary' };

// A Market Report figure as text: "(%)" labels are percents, "Sales per employee" is dollars, ranks and counts are plain.
function marketFigureText(label, value) {
  if (/\(%\)/.test(label)) return percentOf(value);
  if (/^sales per employee$/i.test(label.trim())) return moneyFull(value);
  return plain(value);
}

// The service's summary as plain strings for the printable page. No number is computed here: each one is the
// service's own, formatted. Anything that is not a finite number is left out rather than printed.
export function summaryView(s) {
  const o = s && typeof s === 'object' ? s : {};
  const kind = SUMMARY_TITLES[o.kind] ? o.kind : 'financial_report';
  const market = kind === 'market_report';
  const year = cleanDisplay(o.year, 12);
  const revenueRows = (Array.isArray(o.revenue) ? o.revenue : []).filter((r) => r && isNum(r.value));
  const tiles = [];
  if (revenueRows.length) {
    const y = yearIn(revenueRows[0].label);
    const name = market ? 'Annual sales' : 'Revenue';
    tiles.push({ label: y ? name + ' ' + periodLabel(y, market) : name, value: moneyShort(revenueRows[0].value), note: '' });
  }
  if (o.overall && o.overall.score && isNum(o.overall.score.value)) {
    tiles.push({ label: 'Overall score', value: plain(o.overall.score.value), note: cleanDisplay(o.overall.rating, 16) });
  }
  const cell = (f) => (f && isNum(f.value) ? plain(f.value) + '×' : '—');
  const v = o.valuation && typeof o.valuation === 'object' ? o.valuation : null;
  const mk = o.market && typeof o.market === 'object' ? o.market : null;
  const figures = ((mk && mk.figures) || []).filter((x) => x && isNum(x.value)).map((x) => ({ label: cleanDisplay(x.label, 120), value: marketFigureText(cleanDisplay(x.label, 120), x.value) }));
  return {
    kind,
    title: SUMMARY_TITLES[kind],
    borrower: cleanDisplay(o.borrower && o.borrower.name, 100) || SUMMARY_FALLBACK_NAME,
    subtitle: [slicePart(o.industry), o.area && o.area.name ? cleanDisplay(o.area.name, 120) : '', o.salesClass && o.salesClass.name ? cleanDisplay(o.salesClass.name, 120) : '', year ? periodLabel(year, market) : '']
      .filter(Boolean)
      .join(' · '),
    preparedOn: longDate(o.generatedAt) ? 'Prepared ' + longDate(o.generatedAt) : '',
    tiles,
    variances: (Array.isArray(o.keyVariances) ? o.keyVariances : [])
      .filter((x) => x && isNum(x.yourFirm) && isNum(x.industry) && isNum(x.variancePercent))
      .map((x) => {
        const fmt = VARIANCE_FORMAT[varianceUnit(x)] || plain;
        return {
          title: cleanDisplay(x.title, 80),
          yours: fmt(x.yourFirm),
          industry: fmt(x.industry),
          variance: signed(x.variancePercent),
          rating: cleanDisplay(x.rating, 16),
          tone: ratingTone(x.rating),
        };
      }),
    revenue: revenueRows.map((r) => ({ label: yearIn(r.label) ? periodLabel(yearIn(r.label), market) : cleanDisplay(r.label, 60), value: moneyFull(r.value) })),
    revenueTitle: market ? 'Annual sales by period' : 'Revenue by year',
    market: mk && figures.length ? { period: cleanDisplay(mk.period, 12), figures } : null,
    values: ((v && v.values) || []).filter((x) => x && isNum(x.value)).map((x) => ({ label: cleanDisplay(x.label, 120), value: moneyFull(x.value) })),
    multiples: ((v && v.multiples) || [])
      .filter((m) => m && (m.low || m.median || m.high))
      .map((m) => ({ label: (SUMMARY_APPROACH[m.approach] || 'Multiples') + ', ' + cleanDisplay(m.year, 4), low: cell(m.low), median: cell(m.median), high: cell(m.high) })),
    points: (Array.isArray(o.talkingPoints) ? o.talkingPoints : []).map((p) => cleanDisplay(p && p.text, 400)).filter(Boolean),
    disclaimer: cleanDisplay(o.disclaimer, 400) || DISCLAIMERS[kind],
  };
}

// ---- failed turns ----

// engine_error (a report could not be built, not retryable) points to support; the others carry their own advice.
export const errorNeedsSupport = (code) => code === 'engine_error';

// What the retry button of a failed answer does: 'ask' (the question above is asked again), 'resubmit' (a form
// submission is sent again with what was typed) or null (nothing to resend, or not retryable).
export function retryKind({ error, canAsk, formId, hasValues } = {}) {
  if (!error || error.retryable !== true) return null;
  if (canAsk) return 'ask';
  return formId && hasValues ? 'resubmit' : null;
}
